import React, { useState, useEffect, useRef } from 'react';
import FaceImageCropModal from './FaceImageCropModal';
import AiDesignerChatPanel from './AiDesignerChatPanel';
import { showToast } from '../context/ModalDialogContext';



const reliefFilters = {
  low: { filter: 'drop-shadow(0.5px 0.5px 0.5px rgba(0,0,0,0.5))', textShadow: '0.5px 0.5px 0px rgba(255,255,255,0.2), -0.5px -0.5px 0px rgba(0,0,0,0.6)' },
  medium: { filter: 'drop-shadow(1px 1px 1px rgba(0,0,0,0.6))', textShadow: '1px 1px 0px rgba(255,255,255,0.3), -1px -1px 0px rgba(0,0,0,0.7)' },
  high: { filter: 'drop-shadow(2px 2px 1.5px rgba(0,0,0,0.8))', textShadow: '1.5px 1.5px 0px rgba(255,255,255,0.4), -1.5px -1.5px 0.5px rgba(0,0,0,0.9)' }
};

export const getRingBoundingBox = (ring, textRadius = 75) => {
  if (!ring || typeof ring !== 'object') return null;

  if (ring.id === 'typography-layer' || ring.isCurved) {
    const rawR = ring.id === 'typography-layer' ? textRadius : (ring.textRadius || textRadius || 75);
    const radius = Number(rawR) > 0 ? Number(rawR) : 75;
    const offX = Number(ring.offsetX) || 0;
    const offY = Number(ring.offsetY) || 0;
    return {
      x: 100 - radius + offX,
      y: 100 - radius + offY,
      width: radius * 2,
      height: radius * 2,
      cx: 100 + offX,
      cy: 100 + offY,
      rotation: 0,
      transform: undefined
    };
  }

  if (ring.type === 'image') {
    const w = Number(ring.width) > 0 ? Number(ring.width) : 80;
    const h = Number(ring.height) > 0 ? Number(ring.height) : 80;
    const offX = Number(ring.offsetX) || 0;
    const offY = Number(ring.offsetY) || 0;
    const cx = 100 + offX;
    const cy = 100 + offY;
    const rot = Number(ring.angle) || 0;
    return {
      x: 100 - w / 2 + offX,
      y: 100 - h / 2 + offY,
      width: w,
      height: h,
      cx,
      cy,
      rotation: rot,
      transform: rot ? `rotate(${rot}, ${cx}, ${cy})` : undefined
    };
  }

  if (ring.type === 'text') {
    const fontSize = Number(ring.fontSize) > 0 ? Number(ring.fontSize) : 16;
    const text = String(ring.text || '');
    const len = text.length || 1;
    const hScale = (ring.hScale !== undefined && !isNaN(ring.hScale) ? Number(ring.hScale) : 100) / 100;
    const vScale = (ring.vScale !== undefined && !isNaN(ring.vScale) ? Number(ring.vScale) : 100) / 100;
    const letterSpacing = Number(ring.letterSpacing) || 0;
    const w = Math.max(16, (len * fontSize * 0.6 + letterSpacing * Math.max(0, len - 1)) * hScale);
    const h = Math.max(12, fontSize * 1.2 * vScale);
    const offX = Number(ring.offsetX) || 0;
    const offY = Number(ring.offsetY) || 0;
    const cx = 100 + offX;
    const cy = 100 + offY;
    let x = cx - w / 2;
    if (ring.textAlign === 'left') x = cx;
    else if (ring.textAlign === 'right') x = cx - w;
    const y = cy - h / 2;
    const rot = Number(ring.shear) || 0;
    return {
      x,
      y,
      width: w,
      height: h,
      cx,
      cy,
      rotation: rot,
      transform: rot ? `rotate(${rot}, ${cx}, ${cy})` : undefined
    };
  }

  // Default shape / ring layer
  const rx = (ring.outerRadiusX !== undefined && !isNaN(ring.outerRadiusX))
    ? Number(ring.outerRadiusX)
    : (Number(ring.width) > 0 ? Math.round(Number(ring.width) / 2) : 50);
  const ry = (ring.outerRadiusY !== undefined && !isNaN(ring.outerRadiusY))
    ? Number(ring.outerRadiusY)
    : (Number(ring.height) > 0 ? Math.round(Number(ring.height) / 2) : rx);
  const offX = Number(ring.offsetX) || 0;
  const offY = Number(ring.offsetY) || 0;
  const w = rx * 2;
  const h = ry * 2;
  const cx = 100 + offX;
  const cy = 100 + offY;
  return {
    x: cx - rx,
    y: cy - ry,
    width: w,
    height: h,
    cx,
    cy,
    rotation: 0,
    transform: undefined
  };
};

export const getHandles = (box) => {
  if (!box || isNaN(box.x) || isNaN(box.y) || isNaN(box.width) || isNaN(box.height)) return [];
  const { x, y, width, height } = box;
  const halfW = width / 2;
  const halfH = height / 2;

  return [
    { id: 'nw', x: x, y: y, cursor: 'nwse-resize' },
    { id: 'n', x: x + halfW, y: y, cursor: 'ns-resize' },
    { id: 'ne', x: x + width, y: y, cursor: 'nesw-resize' },
    { id: 'e', x: x + width, y: y + halfH, cursor: 'ew-resize' },
    { id: 'se', x: x + width, y: y + height, cursor: 'nwse-resize' },
    { id: 's', x: x + halfW, y: y + height, cursor: 'ns-resize' },
    { id: 'sw', x: x, y: y + height, cursor: 'nesw-resize' },
    { id: 'w', x: x, y: y + halfH, cursor: 'ew-resize' }
  ];
};

export default function FullscreenFaceEditor({
  user,
  tenantConfig,
  designName,
  setDesignName,
  selectedDesignId,
  metal,
  setMetal,
  faceSide,
  setFaceSide,
  symbol,
  setSymbol,
  upperText,
  setUpperText,
  lowerText,
  setLowerText,
  textMode,
  setTextMode,
  singleText,
  setSingleText,
  textFontWeight,
  setTextFontWeight,
  textFontStyle,
  setTextFontStyle,
  textCase,
  setTextCase,
  edgeStyle,
  setEdgeStyle,
  depth,
  setDepth,
  textGrayscale,
  setTextGrayscale,
  textRadius,
  setTextRadius,
  textSize,
  setTextSize,
  textKerning,
  setTextKerning,
  textFont,
  setTextFont,
  rings,
  setRings,
  selectedRingId,
  setSelectedRingId,
  addRing,
  deleteRing,
  moveRingUp,
  moveRingDown,
  updateSelectedRing,
  handleAssetDoubleClick,
  studioAssets,
  fetchStudioAssets,
  assetsSearch,
  setAssetsSearch,
  assetsType,
  setAssetsType,
  handleSaveConfig,
  handleExportHighResPNG,
  exportTypographySVG,
  setIsFullscreenFaceEditorOpen
}) {
  const [activeTool, setActiveTool] = useState('select'); // 'select' | 'image' | 'ring' | 'text'
  const [activeRightTab, setActiveRightTab] = useState('layers'); // 'layers' | 'properties' | 'settings' | 'emblems'
  const [isSpinning, setIsSpinning] = useState(false);
  const [canvasSize, setCanvasSize] = useState(600);
  const [draggedLayerIndex, setDraggedLayerIndex] = useState(null);
  const [showOpacitySlider, setShowOpacitySlider] = useState(false);
  const [renamingLayerId, setRenamingLayerId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [characterExpanded, setCharacterExpanded] = useState(true);
  const [blurExpanded, setBlurExpanded] = useState(true);
  const [shadowExpanded, setShadowExpanded] = useState(true);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [localSearch, setLocalSearch] = useState('');
  const [localType, setLocalType] = useState('all');
  const [showImageCropModal, setShowImageCropModal] = useState(false);
  const [cropAdjustingRing, setCropAdjustingRing] = useState(null);
  const [isChangingImageSource, setIsChangingImageSource] = useState(false);
  const [changingImageRingId, setChangingImageRingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Asset Upload States matching Card Designer
  const assetFileInputRef = useRef(null);
  const directUploadInputRef = useRef(null);
  const [isUploadingAsset, setIsUploadingAsset] = useState(false);
  const [assetUploadError, setAssetUploadError] = useState('');
  const [assetUploadSuccess, setAssetUploadSuccess] = useState('');

  // Universal Top Banner Slide-in Notification Observers
  useEffect(() => {
    if (assetUploadSuccess) showToast(assetUploadSuccess, 'success');
  }, [assetUploadSuccess]);

  useEffect(() => {
    if (assetUploadError) showToast(assetUploadError, 'error');
  }, [assetUploadError]);

  const [isAssetDragOver, setIsAssetDragOver] = useState(false);
  const [isCanvasDragOver, setIsCanvasDragOver] = useState(false);

  // Local assets cache with optimistic upload support
  const [localStudioAssets, setLocalStudioAssets] = useState(studioAssets || []);
  useEffect(() => {
    if (Array.isArray(studioAssets)) {
      setLocalStudioAssets(prev => {
        const existingIds = new Set(studioAssets.map(a => a.id));
        const customLocal = (prev || []).filter(a => a && !existingIds.has(a.id));
        return [...customLocal, ...studioAssets];
      });
    }
  }, [studioAssets]);

  const token = typeof window !== 'undefined' ? (sessionStorage.getItem('token') || localStorage.getItem('token')) : '';
  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';

  const handleUploadAssetFiles = async (files, options = {}) => {
    if (!files || files.length === 0) return;
    setIsUploadingAsset(true);
    setAssetUploadError('');
    setAssetUploadSuccess('');

    const activeTenantId = user?.tenant_id || tenantConfig?.id;
    let successCount = 0;
    const newlyUploaded = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isImg = file.type.startsWith('image/') || file.name.match(/\.(png|jpg|jpeg|svg|webp|gif)$/i);
      if (!isImg) {
        setAssetUploadError(`File "${file.name}" is not a supported image/SVG format.`);
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('display_name', file.name);

      let url = `${API_BASE}/api/upload?`;
      const params = new URLSearchParams();
      if (user?.id) params.append('associated_user_id', user.id.toString());
      if (activeTenantId) params.append('tenant_id', activeTenantId.toString());
      url += params.toString();

      try {
        const headers = {
          'X-Tenant-Domain': window.location.hostname
        };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const res = await fetch(url, {
          method: 'POST',
          headers,
          body: formData
        });

        if (!res.ok) {
          const errText = await res.text();
          throw new Error(errText || `Upload failed with status ${res.status}`);
        }

        const data = await res.json();
        if (data?.file) {
          const rawPath = data.file.location || data.file.storage_location || '';
          let cleanRelativePath = rawPath;
          if (rawPath.includes('/uploads/')) {
            cleanRelativePath = rawPath.substring(rawPath.indexOf('/uploads/')).split('?')[0];
          }
          const base = API_BASE ? API_BASE.replace(/\/+$/, '') : '';
          const assetUrl = cleanRelativePath.startsWith('http')
            ? cleanRelativePath
            : (cleanRelativePath.startsWith('/') ? `${base}${cleanRelativePath}` : `${base}/${cleanRelativePath}`);

          const newAssetItem = {
            id: data.file.id,
            filename: data.file.filename,
            original_name: data.file.original_name || file.name,
            original_filename: data.file.original_name || file.name,
            location: data.file.location || data.file.storage_location,
            file_url: assetUrl,
            mime_type: data.file.mime_type,
            file_size: data.file.file_size
          };
          newlyUploaded.push(newAssetItem);
          setLocalStudioAssets(prev => [newAssetItem, ...(prev || []).filter(a => a && a.id !== newAssetItem.id)]);
          successCount++;
        }
      } catch (err) {
        console.error('Failed to upload asset:', err);
        setAssetUploadError(`Failed to upload ${file.name}: ${err.message}`);
      }
    }

    setIsUploadingAsset(false);
    if (successCount > 0) {
      setAssetUploadSuccess(`Successfully uploaded ${successCount} asset${successCount > 1 ? 's' : ''}!`);
      setTimeout(() => setAssetUploadSuccess(''), 4000);
      if (fetchStudioAssets) {
        fetchStudioAssets();
      }

      // Check if target ring should be directly replaced
      const targetRingId = options.replaceRingId || (isChangingImageSource ? changingImageRingId : null);
      if (targetRingId && newlyUploaded.length > 0) {
        const first = newlyUploaded[0];
        saveHistoryState();
        let updatedRing = null;
        setRings(prevRings => prevRings.map(r => {
          if (r.id === targetRingId) {
            updatedRing = {
              ...r,
              imageUrl: first.file_url,
              name: first.original_name || r.name,
              imgPanX: 0,
              imgPanY: 0,
              imgZoom: 1,
              cropApplied: false
            };
            return updatedRing;
          }
          return r;
        }));
        setIsChangingImageSource(false);
        setChangingImageRingId(null);
        setShowAssetModal(false);
        if (updatedRing) {
          openImageAdjustModal(updatedRing);
        }
      } else if (options.autoAddToCanvas && newlyUploaded.length > 0) {
        // Dragged directly onto coin face canvas -> auto-add layer to coin
        const first = newlyUploaded[0];
        saveHistoryState();
        handleAssetDoubleClick(first.file_url, first.original_name || first.filename);
        setActiveTool('select');
        setActiveRightTab('properties');
      }
    }
  };

  const [showAiPanel, setShowAiPanel] = useState(() => {
    try {
      return localStorage.getItem('trophycoin_ai_panel_open_face') === 'true';
    } catch (e) {
      return false;
    }
  });
  const [isAiServerAvailable, setIsAiServerAvailable] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem('trophycoin_ai_panel_open_face', showAiPanel ? 'true' : 'false');
    } catch (e) {}
  }, [showAiPanel]);

  const hasGeminiKey = Boolean(
    (tenantConfig?.gemini_api_key || user?.tenant?.gemini_api_key || '').trim()
  ) || isAiServerAvailable;

  useEffect(() => {
    let isMounted = true;
    const checkAiStatus = async () => {
      try {
        const tId = tenantConfig?.id || user?.tenant_id;
        const url = `${API_BASE}/api/v1/ai-designer/status${tId ? `?tenant_id=${tId}` : ''}`;
        const res = await fetch(url, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'X-Tenant-Domain': window.location.hostname
          }
        });
        if (res.ok && isMounted) {
          const data = await res.json();
          setIsAiServerAvailable(Boolean(data.available));
        }
      } catch (e) {}
    };
    checkAiStatus();
    return () => { isMounted = false; };
  }, [tenantConfig, user, token, API_BASE]);

  const handleApplyAiFaceMutations = (mutations) => {
    if (!mutations) return;

    if (mutations.__revert_state) {
      const prev = mutations.__revert_state;
      if (prev.metal && setMetal) setMetal(prev.metal);
      if (prev.edgeStyle && setEdgeStyle) setEdgeStyle(prev.edgeStyle);
      if (prev.upperText !== undefined && setUpperText) setUpperText(prev.upperText);
      if (prev.lowerText !== undefined && setLowerText) setLowerText(prev.lowerText);
      if (prev.textFont && setTextFont) setTextFont(prev.textFont);
      if (prev.textSize !== undefined && setTextSize) setTextSize(prev.textSize);
      if (prev.textRadius !== undefined && setTextRadius) setTextRadius(prev.textRadius);
      if (prev.textKerning !== undefined && setTextKerning) setTextKerning(prev.textKerning);
      if (prev.depth !== undefined && setDepth) setDepth(prev.depth);
      if (prev.rings && setRings) setRings(prev.rings);
      return;
    }

    if (mutations.metal && setMetal) setMetal(mutations.metal);
    if (mutations.edgeStyle && setEdgeStyle) setEdgeStyle(mutations.edgeStyle);
    if (mutations.upperText !== undefined && setUpperText) setUpperText(mutations.upperText);
    if (mutations.lowerText !== undefined && setLowerText) setLowerText(mutations.lowerText);
    if (mutations.textFont && setTextFont) setTextFont(mutations.textFont);
    if (mutations.textSize !== undefined && setTextSize) setTextSize(mutations.textSize);
    if (mutations.textRadius !== undefined && setTextRadius) setTextRadius(mutations.textRadius);
    if (mutations.textKerning !== undefined && setTextKerning) setTextKerning(mutations.textKerning);
    if (mutations.depth !== undefined && setDepth) setDepth(mutations.depth);
    if (mutations.rings && Array.isArray(mutations.rings) && setRings) {
      setRings(mutations.rings);
    }
    if (mutations.suggested_symbol_search && setAssetsSearch) {
      setAssetsSearch(mutations.suggested_symbol_search);
      setActiveRightTab('emblems');
    }
  };

  const openImageAdjustModal = (ring) => {
    const target = ring || rings.find(r => r.id === selectedRingId);
    if (!target || target.type !== 'image') return;
    setSelectedRingId(target.id);
    setCropAdjustingRing(target);
    setShowImageCropModal(true);
  };

  const handleApplyImageCrop = (cropData) => {
    if (!cropAdjustingRing) return;
    saveHistoryState();
    setRings(prevRings => prevRings.map(r => {
      if (r.id === cropAdjustingRing.id) {
        return {
          ...r,
          ...cropData
        };
      }
      return r;
    }));
  };

  const handleResetImageCrop = (ringId) => {
    saveHistoryState();
    setRings(prevRings => prevRings.map(r => {
      if (r.id === ringId) {
        return {
          ...r,
          imgPanX: 0,
          imgPanY: 0,
          imgZoom: 1,
          cropApplied: false
        };
      }
      return r;
    }));
  };

  const dragInfo = useRef(null);

  const getSvgCoords = (e) => {
    const svg = document.getElementById('studio-coin-svg');
    if (!svg) return { x: 0, y: 0 };
    if (typeof svg.createSVGPoint === 'function' && typeof svg.getScreenCTM === 'function') {
      try {
        const ctm = svg.getScreenCTM();
        if (ctm && typeof ctm.inverse === 'function') {
          const pt = svg.createSVGPoint();
          pt.x = e.clientX;
          pt.y = e.clientY;
          const transformed = pt.matrixTransform(ctm.inverse());
          return { x: transformed.x, y: transformed.y };
        }
      } catch (err) {
        // Fallback below
      }
    }
    if (typeof svg.getBoundingClientRect === 'function') {
      const rect = svg.getBoundingClientRect();
      const scaleX = 200 / (rect.width || 1);
      const scaleY = 200 / (rect.height || 1);
      return {
        x: (e.clientX - (rect.left || 0)) * scaleX,
        y: (e.clientY - (rect.top || 0)) * scaleY
      };
    }
    return { x: e.clientX || 0, y: e.clientY || 0 };
  };

  const handleBoundingBoxPointerDown = (e, ring, action = 'drag', handleId = null) => {
    e.stopPropagation();
    if (ring.locked) return;

    if (selectedRingId !== ring.id) {
      setSelectedRingId(ring.id);
      setActiveRightTab('properties');
    }

    saveHistoryState();

    const startPt = getSvgCoords(e);
    dragInfo.current = {
      isDragging: action === 'drag',
      isResizing: action === 'resize',
      handle: handleId,
      startSvgX: startPt.x,
      startSvgY: startPt.y,
      ringId: ring.id,
      ringType: ring.type,
      isCurved: ring.id === 'typography-layer' || ring.isCurved,
      startOffsetX: ring.offsetX || 0,
      startOffsetY: ring.offsetY || 0,
      startOuterRadiusX: ring.outerRadiusX !== undefined ? ring.outerRadiusX : (ring.width ? Math.round(ring.width / 2) : 50),
      startOuterRadiusY: ring.outerRadiusY !== undefined ? ring.outerRadiusY : (ring.height ? Math.round(ring.height / 2) : (ring.outerRadiusX || 50)),
      startWidth: ring.width || 80,
      startHeight: ring.height || 80,
      startTextRadius: ring.id === 'typography-layer' ? textRadius : (ring.textRadius || textRadius || 75),
      startFontSize: ring.fontSize || 16,
      constrainOuter: ring.constrainOuter !== false,
      constrainSize: ring.constrainSize !== false
    };

    document.addEventListener('pointermove', handleBoundingBoxPointerMove);
    document.addEventListener('pointerup', handleBoundingBoxPointerUp);
  };

  const handleBoundingBoxPointerMove = (e) => {
    const info = dragInfo.current;
    if (!info || (!info.isDragging && !info.isResizing)) return;

    const currentPt = getSvgCoords(e);
    const dx = currentPt.x - info.startSvgX;
    const dy = currentPt.y - info.startSvgY;

    if (info.isDragging) {
      if (info.isCurved && info.ringId === 'typography-layer') {
        return;
      }
      const nextOffsetX = Math.round(info.startOffsetX + dx);
      const nextOffsetY = Math.round(info.startOffsetY + dy);

      setRings(prevRings => prevRings.map(r => {
        if (r.id === info.ringId) {
          return { ...r, offsetX: nextOffsetX, offsetY: nextOffsetY };
        }
        return r;
      }));
    } else if (info.isResizing) {
      const handle = info.handle;

      if (info.isCurved) {
        const dist = Math.sqrt(Math.pow(currentPt.x - 100, 2) + Math.pow(currentPt.y - 100, 2));
        const clampedRadius = Math.max(15, Math.min(95, Math.round(dist)));
        if (info.ringId === 'typography-layer') {
          setTextRadius(clampedRadius);
        } else {
          setRings(prevRings => prevRings.map(r => {
            if (r.id === info.ringId) {
              return { ...r, textRadius: clampedRadius };
            }
            return r;
          }));
        }
      } else if (info.ringType === 'image') {
        let dw = 0;
        let dh = 0;
        if (handle.includes('e')) dw += dx * 2;
        if (handle.includes('w')) dw -= dx * 2;
        if (handle.includes('s')) dh += dy * 2;
        if (handle.includes('n')) dh -= dy * 2;

        let newW = Math.max(10, Math.min(200, Math.round(info.startWidth + dw)));
        let newH = Math.max(10, Math.min(200, Math.round(info.startHeight + dh)));

        if (info.constrainSize) {
          const delta = Math.abs(dw) > Math.abs(dh) ? dw : dh;
          const uniformSize = Math.max(10, Math.min(200, Math.round(info.startWidth + delta)));
          newW = uniformSize;
          newH = uniformSize;
        }

        setRings(prevRings => prevRings.map(r => {
          if (r.id === info.ringId) {
            return {
              ...r,
              width: newW,
              height: newH,
              maskRadius: Math.round(newW / 2)
            };
          }
          return r;
        }));
      } else if (info.ringType === 'text') {
        let dw = 0;
        let dh = 0;
        if (handle.includes('e')) dw += dx;
        if (handle.includes('w')) dw -= dx;
        if (handle.includes('s')) dh += dy;
        if (handle.includes('n')) dh -= dy;
        const delta = Math.abs(dw) > Math.abs(dh) ? dw : dh;
        const newFontSize = Math.max(6, Math.min(80, Math.round(info.startFontSize + delta * 0.4)));
        setRings(prevRings => prevRings.map(r => {
          if (r.id === info.ringId) {
            return { ...r, fontSize: newFontSize };
          }
          return r;
        }));
      } else {
        // Shape / Ring layer (outerRadiusX, outerRadiusY)
        let drx = 0;
        let dry = 0;
        if (handle.includes('e')) drx += dx;
        if (handle.includes('w')) drx -= dx;
        if (handle.includes('s')) dry += dy;
        if (handle.includes('n')) dry -= dy;

        let nextRx = Math.max(5, Math.min(100, Math.round(info.startOuterRadiusX + drx)));
        let nextRy = Math.max(5, Math.min(100, Math.round(info.startOuterRadiusY + dry)));

        if (info.constrainOuter) {
          const maxDelta = Math.abs(drx) > Math.abs(dry) ? drx : dry;
          const uniformR = Math.max(5, Math.min(100, Math.round(info.startOuterRadiusX + maxDelta)));
          nextRx = uniformR;
          nextRy = uniformR;
        }

        setRings(prevRings => prevRings.map(r => {
          if (r.id === info.ringId) {
            return {
              ...r,
              outerRadiusX: nextRx,
              outerRadiusY: nextRy
            };
          }
          return r;
        }));
      }
    }
  };

  const handleBoundingBoxPointerUp = () => {
    dragInfo.current = null;
    document.removeEventListener('pointermove', handleBoundingBoxPointerMove);
    document.removeEventListener('pointerup', handleBoundingBoxPointerUp);
  };

  useEffect(() => {
    return () => {
      document.removeEventListener('pointermove', handleBoundingBoxPointerMove);
      document.removeEventListener('pointerup', handleBoundingBoxPointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveHistoryState = (newRings, optionalGlobals = {}) => {
    let targetRings = newRings;
    if (newRings && (newRings.nativeEvent || newRings instanceof Event || typeof newRings.preventDefault === 'function')) {
      targetRings = undefined;
    }
    const currentGlobals = {
      singleText,
      upperText,
      lowerText,
      textMode,
      textRadius,
      textSize,
      textKerning,
      textFont,
      textFontWeight,
      textFontStyle,
      textCase,
      textGrayscale,
      selectedRingId,
      activeRightTab,
      activeTool,
      ...optionalGlobals
    };
    
    const stateStr = JSON.stringify({
      rings: targetRings || rings,
      globals: currentGlobals
    });
    
    setUndoStack(prev => [...prev, stateStr]);
    setRedoStack([]);
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    
    const currentGlobals = {
      singleText,
      upperText,
      lowerText,
      textMode,
      textRadius,
      textSize,
      textKerning,
      textFont,
      textFontWeight,
      textFontStyle,
      textCase,
      textGrayscale,
      selectedRingId,
      activeRightTab,
      activeTool
    };
    const currentStateStr = JSON.stringify({
      rings,
      globals: currentGlobals
    });
    setRedoStack(prev => [...prev, currentStateStr]);
    
    const previousStateStr = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    
    const parsed = JSON.parse(previousStateStr);
    setRings(parsed.rings);
    
    if (parsed.globals) {
      if (parsed.globals.singleText !== undefined) setSingleText(parsed.globals.singleText);
      if (parsed.globals.upperText !== undefined) setUpperText(parsed.globals.upperText);
      if (parsed.globals.lowerText !== undefined) setLowerText(parsed.globals.lowerText);
      if (parsed.globals.textMode !== undefined) setTextMode(parsed.globals.textMode);
      if (parsed.globals.textRadius !== undefined) setTextRadius(parsed.globals.textRadius);
      if (parsed.globals.textSize !== undefined) setTextSize(parsed.globals.textSize);
      if (parsed.globals.textKerning !== undefined) setTextKerning(parsed.globals.textKerning);
      if (parsed.globals.textFont !== undefined) setTextFont(parsed.globals.textFont);
      if (parsed.globals.textFontWeight !== undefined) setTextFontWeight(parsed.globals.textFontWeight);
      if (parsed.globals.textFontStyle !== undefined) setTextFontStyle(parsed.globals.textFontStyle);
      if (parsed.globals.textCase !== undefined) setTextCase(parsed.globals.textCase);
      if (parsed.globals.textGrayscale !== undefined) setTextGrayscale(parsed.globals.textGrayscale);
      if (parsed.globals.selectedRingId !== undefined) setSelectedRingId(parsed.globals.selectedRingId);
      if (parsed.globals.activeRightTab !== undefined) setActiveRightTab(parsed.globals.activeRightTab);
      if (parsed.globals.activeTool !== undefined) setActiveTool(parsed.globals.activeTool);
    }
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    
    const currentGlobals = {
      singleText,
      upperText,
      lowerText,
      textMode,
      textRadius,
      textSize,
      textKerning,
      textFont,
      textFontWeight,
      textFontStyle,
      textCase,
      textGrayscale,
      selectedRingId,
      activeRightTab,
      activeTool
    };
    const currentStateStr = JSON.stringify({
      rings,
      globals: currentGlobals
    });
    setUndoStack(prev => [...prev, currentStateStr]);
    
    const nextStateStr = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    
    const parsed = JSON.parse(nextStateStr);
    setRings(parsed.rings);
    
    if (parsed.globals) {
      if (parsed.globals.singleText !== undefined) setSingleText(parsed.globals.singleText);
      if (parsed.globals.upperText !== undefined) setUpperText(parsed.globals.upperText);
      if (parsed.globals.lowerText !== undefined) setLowerText(parsed.globals.lowerText);
      if (parsed.globals.textMode !== undefined) setTextMode(parsed.globals.textMode);
      if (parsed.globals.textRadius !== undefined) setTextRadius(parsed.globals.textRadius);
      if (parsed.globals.textSize !== undefined) setTextSize(parsed.globals.textSize);
      if (parsed.globals.textKerning !== undefined) setTextKerning(parsed.globals.textKerning);
      if (parsed.globals.textFont !== undefined) setTextFont(parsed.globals.textFont);
      if (parsed.globals.textFontWeight !== undefined) setTextFontWeight(parsed.globals.textFontWeight);
      if (parsed.globals.textFontStyle !== undefined) setTextFontStyle(parsed.globals.textFontStyle);
      if (parsed.globals.textCase !== undefined) setTextCase(parsed.globals.textCase);
      if (parsed.globals.textGrayscale !== undefined) setTextGrayscale(parsed.globals.textGrayscale);
      if (parsed.globals.selectedRingId !== undefined) setSelectedRingId(parsed.globals.selectedRingId);
      if (parsed.globals.activeRightTab !== undefined) setActiveRightTab(parsed.globals.activeRightTab);
      if (parsed.globals.activeTool !== undefined) setActiveTool(parsed.globals.activeTool);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Do not nudge or intercept shortcuts when typing inside form controls
      const activeEl = document.activeElement;
      const tag = activeEl ? activeEl.tagName.toUpperCase() : '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (activeEl && activeEl.isContentEditable)) {
        return;
      }

      if (showAssetModal || showImageCropModal) return;

      // Keyboard Undo / Redo
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        handleRedo();
        return;
      }

      // Arrow-key bumping: only active when select tool is active and a ring is selected
      if (activeTool !== 'select' || !selectedRingId) return;

      const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      if (!arrowKeys.includes(e.key)) return;

      e.preventDefault();

      const currentRings = Array.isArray(rings) ? rings : [];
      const target = currentRings.find(r => r && r.id === selectedRingId);
      if (!target || target.locked) return;

      const amount = e.shiftKey ? 10 : 1;
      let dx = 0;
      let dy = 0;

      if (e.key === 'ArrowUp') dy = -amount;
      if (e.key === 'ArrowDown') dy = amount;
      if (e.key === 'ArrowLeft') dx = -amount;
      if (e.key === 'ArrowRight') dx = amount;

      saveHistoryState();

      setRings(prevRings => {
        const current = Array.isArray(prevRings) ? prevRings : currentRings;
        return current.map(r => {
          if (!r) return r;
          const isSelf = r.id === selectedRingId;
          const isChild = r.parentId === selectedRingId;
          if ((isSelf || isChild) && !r.locked) {
            return {
              ...r,
              offsetX: (Number(r.offsetX) || 0) + dx,
              offsetY: (Number(r.offsetY) || 0) + dy
            };
          }
          return r;
        });
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool, selectedRingId, rings, showAssetModal, showImageCropModal, undoStack, redoStack]);

  const updateSelectedRingWithHistory = (key, value) => {
    saveHistoryState();
    updateSelectedRing(key, value);
  };

  const handleDragStart = (e, index) => {
    setDraggedLayerIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedLayerIndex === null || draggedLayerIndex === targetIndex) return;

    saveHistoryState();

    const clonedRings = [...rings];
    const [draggedItem] = clonedRings.splice(draggedLayerIndex, 1);

    const targetItem = clonedRings[targetIndex];
    if (targetItem) {
      if (targetItem.type === 'group') {
        draggedItem.parentId = targetItem.id;
      } else if (targetItem.parentId) {
        draggedItem.parentId = targetItem.parentId;
      } else {
        delete draggedItem.parentId;
      }
    }

    clonedRings.splice(targetIndex, 0, draggedItem);
    setRings(clonedRings);
    setDraggedLayerIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedLayerIndex(null);
  };

  const handleContainerDrop = (e) => {
    e.preventDefault();
    if (draggedLayerIndex === null) return;
    saveHistoryState();
    const clonedRings = [...rings];
    const [draggedItem] = clonedRings.splice(draggedLayerIndex, 1);
    delete draggedItem.parentId;
    clonedRings.push(draggedItem);
    setRings(clonedRings);
    setDraggedLayerIndex(null);
  };

  const addGroup = () => {
    const newGroup = {
      id: `group-${Date.now()}`,
      name: 'New Folder',
      type: 'group',
      collapsed: false,
      visible: true,
      locked: false
    };
    setRings([newGroup, ...rings]);
    setSelectedRingId(newGroup.id);
  };

  const duplicateSelectedRing = () => {
    if (!selectedRingId) return;
    const ringToDuplicate = rings.find(r => r.id === selectedRingId);
    if (!ringToDuplicate) return;

    saveHistoryState();

    const isCurved = ringToDuplicate.id === 'typography-layer' || ringToDuplicate.isCurved;
    const nextId = `${ringToDuplicate.type || 'ring'}-${Date.now()}`;

    const newRing = {
      ...ringToDuplicate,
      id: nextId,
      name: `${ringToDuplicate.name} (Copy)`
    };

    if (isCurved) {
      newRing.isCurved = true;
      newRing.textMode = ringToDuplicate.id === 'typography-layer' ? textMode : (ringToDuplicate.textMode || 'single');
      newRing.singleText = ringToDuplicate.id === 'typography-layer' ? singleText : (ringToDuplicate.singleText || '');
      newRing.upperText = ringToDuplicate.id === 'typography-layer' ? upperText : (ringToDuplicate.upperText || '');
      newRing.lowerText = ringToDuplicate.id === 'typography-layer' ? lowerText : (ringToDuplicate.lowerText || '');
      newRing.textRadius = ringToDuplicate.id === 'typography-layer' ? textRadius : (ringToDuplicate.textRadius || 68);
      newRing.fontSize = ringToDuplicate.id === 'typography-layer' ? textSize : (ringToDuplicate.fontSize || 16);
      newRing.fontWeight = ringToDuplicate.id === 'typography-layer' ? textFontWeight : (ringToDuplicate.fontWeight || '900');
      newRing.fontStyle = ringToDuplicate.id === 'typography-layer' ? textFontStyle : (ringToDuplicate.fontStyle || 'normal');
      newRing.letterSpacing = ringToDuplicate.id === 'typography-layer' ? textKerning : (ringToDuplicate.letterSpacing !== undefined ? ringToDuplicate.letterSpacing : 1.2);
      newRing.fontFamily = ringToDuplicate.id === 'typography-layer' ? textFont : (ringToDuplicate.fontFamily || '"Arial Black", sans-serif');
      newRing.textCase = ringToDuplicate.id === 'typography-layer' ? textCase : (ringToDuplicate.textCase || 'uppercase');
      newRing.grayscale = ringToDuplicate.id === 'typography-layer' ? textGrayscale : (ringToDuplicate.grayscale !== undefined ? ringToDuplicate.grayscale : 100);
    }

    const idx = rings.indexOf(ringToDuplicate);
    const clonedRings = [...rings];
    clonedRings.splice(idx, 0, newRing);
    setRings(clonedRings);
    setSelectedRingId(newRing.id);
  };

  const toggleGroupCollapse = (groupId) => {
    setRings(rings.map(r => r.id === groupId ? { ...r, collapsed: !r.collapsed } : r));
  };

  const getElementDepth = (e) => {
    let depth = 0;
    let currParentId = e.parentId;
    while (currParentId) {
      const targetId = currParentId;
      const parent = rings.find(el => el.id === targetId);
      if (parent) {
        depth++;
        currParentId = parent.parentId;
      } else {
        break;
      }
    }
    return depth;
  };

  const isLayerVisibleInSVG = (r) => {
    if (!r || r.visible === false) return false;
    let currParentId = r.parentId;
    let depth = 0;
    const safeRings = Array.isArray(rings) ? rings : [];
    while (currParentId && depth < 25) {
      depth++;
      const targetId = currParentId;
      const parent = safeRings.find(el => el && el.id === targetId);
      if (parent) {
        if (parent.visible === false) return false;
        currParentId = parent.parentId;
      } else {
        break;
      }
    }
    return true;
  };



  const formatTextCase = (txt, customCase) => {
    if (!txt) return '';
    const activeCase = customCase !== undefined ? customCase : textCase;
    if (activeCase === 'uppercase') return txt.toUpperCase();
    if (activeCase === 'lowercase') return txt.toLowerCase();
    if (activeCase === 'capitalize') {
      return txt.replace(/\b\w/g, char => char.toUpperCase());
    }
    return txt;
  };

  const isTextLayerLocked = () => {
    const textLayer = rings.find(r => r.id === 'typography-layer');
    return textLayer ? !!textLayer.locked : false;
  };

  const canvasWrapperRef = useRef(null);

  // Metal configurations
  const brightness = 255 - Math.round((textGrayscale / 100) * 255);
  const textColor = `rgb(${brightness}, ${brightness}, ${brightness})`;
  const currentRelief = reliefFilters[depth] || reliefFilters.medium;

  useEffect(() => {
    if (!canvasWrapperRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        const size = Math.min(width, height, 480);
        if (size > 0) {
          setCanvasSize(size);
        }
      }
    });
    resizeObserver.observe(canvasWrapperRef.current);
    return () => resizeObserver.disconnect();
  }, []);



  const triggerFastSpin = () => {
    if (isSpinning) return;
    setIsSpinning(true);
    setTimeout(() => setIsSpinning(false), 800);
  };

  const addTextLayer = () => {
    const newLayer = {
      id: `text-${Date.now()}`,
      name: `Text ${rings.filter(r => r.type === 'text' && r.id !== 'typography-layer').length + 1}`,
      type: 'text',
      text: 'Text Layer',
      fontFamily: '"Arial Black", sans-serif',
      fontSize: 16,
      fontWeight: 'normal',
      fontStyle: 'normal',
      letterSpacing: 0,
      textAlign: 'center',
      textDecoration: 'none',
      strokeWidth: 0,
      leading: 1.1,
      baselineShift: 0,
      shear: 0,
      hScale: 100,
      vScale: 100,
      offsetX: 0,
      offsetY: 0,
      visible: true,
      locked: false,
      enableBlur: false,
      gaussianBlur: 0,
      shadowEnabled: false,
      shadowOffsetX: 2,
      shadowOffsetY: 2,
      shadowBlur: 3,
      shadowColor: '#000000',
      shadowOpacity: 0.5
    };
    setRings([...rings, newLayer]);
    setSelectedRingId(newLayer.id);
    setActiveTool('select');
    setActiveRightTab('properties');
  };

  const addTypographyLayer = () => {
    // If it already exists, just select it
    if (rings.some(r => r.id === 'typography-layer')) {
      setSelectedRingId('typography-layer');
      setActiveTool('select');
      setActiveRightTab('properties');
      return;
    }

    const newLayer = {
      id: 'typography-layer',
      name: 'Typography',
      type: 'text',
      visible: true,
      locked: false,
      enableBlur: false,
      gaussianBlur: 0,
      shadowEnabled: false,
      shadowOffsetX: 2,
      shadowOffsetY: 2,
      shadowBlur: 3,
      shadowColor: '#000000',
      shadowOpacity: 0.5
    };
    setRings([newLayer, ...rings]);
    setSelectedRingId('typography-layer');
    setActiveTool('select');
    setActiveRightTab('properties');
  };

  const handleToolClick = (toolId) => {
    if (toolId === 'select') {
      setActiveTool(toolId);
      setActiveRightTab('properties');
    } else if (toolId === 'image') {
      if (setAssetsSearch) setAssetsSearch('');
      if (setAssetsType) setAssetsType('all');
      setLocalSearch('');
      setLocalType('all');
      setShowAssetModal(true);
    } else if (toolId === 'assets') {
      if (setAssetsSearch) setAssetsSearch('');
      if (setAssetsType) setAssetsType('all');
      setLocalSearch('');
      setLocalType('all');
      setShowAssetModal(true);
    } else if (toolId === 'ring') {
      saveHistoryState();
      addRing();
      setActiveTool('select');
      setActiveRightTab('properties');
    } else if (toolId === 'text') {
      saveHistoryState();
      addTextLayer();
    } else if (toolId === 'typography') {
      addTypographyLayer();
    }
  };

  const safeRings = Array.isArray(rings) ? rings.filter(Boolean) : [];
  const activeRing = safeRings.find(r => r && r.id === selectedRingId);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 999999, // On top of everything
      backgroundColor: '#1a1a1c',
      color: '#dfdfe3',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      overflow: 'hidden',
      userSelect: 'none'
    }}>
      {/* 1. TOP BAR / HEADER */}
      <div style={{
        height: '42px',
        backgroundColor: '#2b2b2f',
        borderBottom: '1px solid #3c3c42',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 12px',
        gap: '12px'
      }}>
        {/* Left Side: Exit, Save & Exit, & Title Name Input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              type="button"
              onClick={() => setIsFullscreenFaceEditorOpen(false)}
              data-testid="face-exit-btn"
              style={{
                height: '28px',
                background: 'none',
                border: '1px solid #55555c',
                color: '#ffffff',
                borderRadius: '4px',
                padding: '0 10px',
                fontSize: '11px',
                cursor: 'pointer',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                backgroundColor: '#38383d',
                flexShrink: 0
              }}
            >
              ◀ Exit
            </button>

            <button
              type="button"
              disabled={isSaving}
              data-testid="face-save-exit-btn"
              onClick={async (e) => {
                setIsSaving(true);
                try {
                  await handleSaveConfig(e);
                  setIsFullscreenFaceEditorOpen(false);
                } catch (err) {
                  console.error("Save failed:", err);
                } finally {
                  setIsSaving(false);
                }
              }}
              style={{
                height: '28px',
                backgroundColor: isSaving ? '#0958d9' : '#1677ff',
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                padding: '0 12px',
                fontSize: '11px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontWeight: '700',
                opacity: isSaving ? 0.8 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                flexShrink: 0
              }}
            >
              {isSaving ? 'Saving...' : 'Save & Exit'}
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '11px', color: '#8e8e93', fontWeight: 'bold' }}>
              Face Design Name:
            </span>
            <input
              type="text"
              value={designName}
              onChange={(e) => setDesignName(e.target.value)}
              style={{
                backgroundColor: '#1b1b1d',
                border: '1px solid #3c3c42',
                color: '#ffffff',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold',
                width: '180px'
              }}
            />
            
            {/* Undo / Redo Buttons next to design name */}
            <div style={{ display: 'flex', gap: '4px', marginLeft: '6px', alignItems: 'center' }}>
              <button
                type="button"
                onClick={handleUndo}
                disabled={undoStack.length === 0}
                title="Undo"
                style={{
                  color: undoStack.length === 0 ? '#4b5563' : '#a2a2a8',
                  border: '1px solid ' + (undoStack.length === 0 ? '#27272a' : '#3c3c42'),
                  borderRadius: '4px',
                  width: '28px',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: undoStack.length === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: undoStack.length === 0 ? 'transparent' : 'rgba(255,255,255,0.03)'
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                  <path d="M3 7v6h6"></path>
                  <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"></path>
                </svg>
              </button>
              <button
                type="button"
                onClick={handleRedo}
                disabled={redoStack.length === 0}
                title="Redo"
                style={{
                  color: redoStack.length === 0 ? '#4b5563' : '#a2a2a8',
                  border: '1px solid ' + (redoStack.length === 0 ? '#27272a' : '#3c3c42'),
                  borderRadius: '4px',
                  width: '28px',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: redoStack.length === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: redoStack.length === 0 ? 'transparent' : 'rgba(255,255,255,0.03)'
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                  <path d="M21 7v6h-6"></path>
                  <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7"></path>
                </svg>
              </button>

              {/* Heads / Tails Toggle */}
              <div style={{
                display: 'flex',
                border: '1px solid #3c3c42',
                borderRadius: '4px',
                overflow: 'hidden',
                marginLeft: '8px',
                height: '28px'
              }}>
                <button
                  type="button"
                  onClick={() => setFaceSide('heads')}
                  style={{
                    backgroundColor: faceSide === 'heads' ? '#38383d' : 'transparent',
                    color: faceSide === 'heads' ? '#ffffff' : '#a2a2a8',
                    border: 'none',
                    padding: '0 10px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  Heads
                </button>
                <button
                  type="button"
                  onClick={() => setFaceSide('tails')}
                  style={{
                    backgroundColor: faceSide === 'tails' ? '#38383d' : 'transparent',
                    color: faceSide === 'tails' ? '#ffffff' : '#a2a2a8',
                    border: 'none',
                    padding: '0 10px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    borderLeft: '1px solid #3c3c42'
                  }}
                >
                  Tails
                </button>
              </div>

              {/* Assets Library & Upload Button (Parity with Card Designer) */}
              <button
                type="button"
                onClick={() => {
                  setIsChangingImageSource(false);
                  setChangingImageRingId(null);
                  if (setAssetsSearch) setAssetsSearch('');
                  if (setAssetsType) setAssetsType('all');
                  setLocalSearch('');
                  setLocalType('all');
                  setShowAssetModal(true);
                }}
                data-testid="topbar-assets-btn"
                title="Open Asset Library & Upload New Assets (PNG, JPG, SVG)"
                style={{
                  height: '28px',
                  boxSizing: 'border-box',
                  backgroundColor: showAssetModal ? '#1677ff' : '#38383d',
                  color: '#ffffff',
                  border: '1px solid #55555c',
                  borderRadius: '4px',
                  padding: '0 10px',
                  fontSize: '11px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '5px',
                  flexShrink: 0,
                  marginLeft: '8px',
                  transition: 'all 0.15s'
                }}
                onMouseEnter={(e) => { if (!showAssetModal) e.currentTarget.style.backgroundColor = '#45454b'; }}
                onMouseLeave={(e) => { if (!showAssetModal) e.currentTarget.style.backgroundColor = '#38383d'; }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                <span>Assets</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Side: Status indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '11px', color: '#8e8e93', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#52c41a', display: 'inline-block' }} />
            <span>Face Designer</span>
          </span>
        </div>
      </div>

      {/* MAIN BODY AREA */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* 2. LEFT TOOLBAR */}
        <div style={{
          width: '48px',
          backgroundColor: '#232326',
          borderRight: '1px solid #3c3c42',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '8px 0',
          gap: '8px',
          zIndex: 5,
          position: 'relative'
        }}>
          {[
            {
              id: 'select',
              label: 'Selection tool',
              svg: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="5 3 19 12 12 14 9 21 5 3" />
                </svg>
              )
            },
            {
              id: 'image',
              label: 'Image tool',
              svg: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="2" y1="20" x2="22" y2="20" />
                  <polyline points="12 13 17 8 22 13" />
                  <polyline points="2 13 7 8 13 13" />
                </svg>
              )
            },
            {
              id: 'assets',
              label: 'Asset Library & Upload',
              svg: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
              )
            },
            {
              id: 'ring',
              label: 'Ring tool',
              svg: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                </svg>
              )
            },
            {
              id: 'text',
              label: 'Text tool',
              svg: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4 7 4 4 20 4 20 7" />
                  <line x1="9" y1="20" x2="15" y2="20" />
                  <line x1="12" y1="4" x2="12" y2="20" />
                </svg>
              )
            },
            {
              id: 'typography',
              label: 'Curved Text tool',
              svg: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                  <path d="M4 15a8 8 0 0 1 16 0" />
                  <path d="M10 8h4" />
                  <path d="M12 8v5" />
                </svg>
              )
            }
          ].map(t => {
            const isActive = activeTool === t.id || ((t.id === 'assets' || t.id === 'image') && showAssetModal);
            return (
              <button
                key={t.id}
                data-testid={`tool-${t.id}`}
                type="button"
                onClick={() => handleToolClick(t.id)}
                title={t.label}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: isActive ? '#dfdfe3' : 'transparent',
                  color: isActive ? '#18181b' : '#a2a2a8',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.1s'
                }}
              >
                {t.svg}
              </button>
            );
          })}

          {/* AI Copilot Tool Button (Only available if gemini_api_key is set) */}
          {hasGeminiKey && (
            <>
              <div style={{ width: '28px', height: '1px', backgroundColor: '#3c3c42', margin: '4px 0' }} />
              <button
                type="button"
                id="ai-face-tool-btn"
                onClick={() => setShowAiPanel(prev => !prev)}
                title={showAiPanel ? "Close AI Face Copilot" : "Open AI Face Copilot"}
                style={{
                  position: 'relative',
                  width: '32px',
                  height: '32px',
                  borderRadius: '6px',
                  border: showAiPanel ? '1px solid #bdb2ff' : 'none',
                  backgroundColor: showAiPanel ? 'rgba(189, 178, 255, 0.22)' : 'transparent',
                  color: showAiPanel ? '#bdb2ff' : '#a2a2a8',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s',
                  boxShadow: showAiPanel ? '0 0 10px rgba(189, 178, 255, 0.35)' : 'none'
                }}
                onMouseEnter={e => {
                  if (!showAiPanel) {
                    e.currentTarget.style.color = '#dfdfe3';
                    e.currentTarget.style.backgroundColor = '#38383e';
                  }
                }}
                onMouseLeave={e => {
                  if (!showAiPanel) {
                    e.currentTarget.style.color = '#a2a2a8';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
              </button>
            </>
          )}

          {/* Selection Flyout Panel */}
          {activeTool === 'select' && activeRing && (
            <div
              id="layout-flyout-menu"
              style={{
                position: 'absolute',
                left: '52px',
                top: '8px',
                backgroundColor: '#2b2b2f',
                border: '1px solid #3c3c42',
                borderRadius: '6px',
                padding: '8px 10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                zIndex: 100,
                width: '144px',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}
            >
              {/* Section 1: Align to Canvas */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Align To Canvas
                </span>
                
                {/* Grid of Align Buttons */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 32px)', gap: '6px', justifyContent: 'center' }}>
                  <button
                    type="button"
                    onClick={() => {
                      if (activeRing.type === 'image') {
                        updateSelectedRing('offsetX', -100 + activeRing.width / 2);
                      } else {
                        updateSelectedRing('offsetX', -100 + activeRing.outerRadiusX);
                      }
                    }}
                    title="Align Left"
                    style={{
                      backgroundColor: '#1b1b1d',
                      border: '1px solid #3c3c42',
                      borderRadius: '4px',
                      color: '#dfdfe3',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="3" y1="3" x2="3" y2="21" />
                      <rect x="9" y="5" width="12" height="6" rx="1" />
                      <rect x="9" y="13" width="8" height="6" rx="1" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      updateSelectedRing('offsetX', 0);
                    }}
                    title="Align Horiz. Center"
                    style={{
                      backgroundColor: '#1b1b1d',
                      border: '1px solid #3c3c42',
                      borderRadius: '4px',
                      color: '#dfdfe3',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="3" x2="12" y2="21" />
                      <rect x="6" y="5" width="12" height="6" rx="1" />
                      <rect x="8" y="13" width="8" height="6" rx="1" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (activeRing.type === 'image') {
                        updateSelectedRing('offsetX', 100 - activeRing.width / 2);
                      } else {
                        updateSelectedRing('offsetX', 100 - activeRing.outerRadiusX);
                      }
                    }}
                    title="Align Right"
                    style={{
                      backgroundColor: '#1b1b1d',
                      border: '1px solid #3c3c42',
                      borderRadius: '4px',
                      color: '#dfdfe3',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="21" y1="3" x2="21" y2="21" />
                      <rect x="3" y="5" width="12" height="6" rx="1" />
                      <rect x="7" y="13" width="8" height="6" rx="1" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (activeRing.type === 'image') {
                        updateSelectedRing('offsetY', -100 + activeRing.height / 2);
                      } else {
                        updateSelectedRing('offsetY', -100 + activeRing.outerRadiusY);
                      }
                    }}
                    title="Align Top"
                    style={{
                      backgroundColor: '#1b1b1d',
                      border: '1px solid #3c3c42',
                      borderRadius: '4px',
                      color: '#dfdfe3',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="3" y1="3" x2="21" y2="3" />
                      <rect x="5" y="9" width="6" height="12" rx="1" />
                      <rect x="13" y="9" width="6" height="8" rx="1" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      updateSelectedRing('offsetY', 0);
                    }}
                    title="Align Vert. Center"
                    style={{
                      backgroundColor: '#1b1b1d',
                      border: '1px solid #3c3c42',
                      borderRadius: '4px',
                      color: '#dfdfe3',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <rect x="5" y="6" width="6" height="12" rx="1" />
                      <rect x="13" y="8" width="6" height="8" rx="1" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (activeRing.type === 'image') {
                        updateSelectedRing('offsetY', 100 - activeRing.height / 2);
                      } else {
                        updateSelectedRing('offsetY', 100 - activeRing.outerRadiusY);
                      }
                    }}
                    title="Align Bottom"
                    style={{
                      backgroundColor: '#1b1b1d',
                      border: '1px solid #3c3c42',
                      borderRadius: '4px',
                      color: '#dfdfe3',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="3" y1="21" x2="21" y2="21" />
                      <rect x="5" y="3" width="6" height="12" rx="1" />
                      <rect x="13" y="7" width="6" height="8" rx="1" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Section 2: Geometry Stretch Tools */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid #3c3c42', paddingTop: '6px' }}>
                <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Geometry
                </span>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      updateSelectedRing('offsetX', 0);
                      if (activeRing.type === 'image') {
                        updateSelectedRing('width', 200);
                      } else {
                        updateSelectedRing('outerRadiusX', 100);
                      }
                    }}
                    title="Stretch Width to Fit Canvas"
                    style={{
                      flex: 1,
                      backgroundColor: '#1b1b1d',
                      border: '1px solid #3c3c42',
                      borderRadius: '3px',
                      color: '#dfdfe3',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: '9px',
                      fontWeight: 'bold',
                      gap: '3px'
                    }}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="5 9 2 12 5 15" />
                      <polyline points="19 9 22 12 19 15" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                    </svg>
                    Width
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      updateSelectedRing('offsetY', 0);
                      if (activeRing.type === 'image') {
                        updateSelectedRing('height', 200);
                      } else {
                        updateSelectedRing('outerRadiusY', 100);
                      }
                    }}
                    title="Stretch Height to Fit Canvas"
                    style={{
                      flex: 1,
                      backgroundColor: '#1b1b1d',
                      border: '1px solid #3c3c42',
                      borderRadius: '3px',
                      color: '#dfdfe3',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: '9px',
                      fontWeight: 'bold',
                      gap: '3px'
                    }}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 5 12 2 15 5" />
                      <polyline points="9 19 12 22 15 19" />
                      <line x1="12" y1="2" x2="12" y2="22" />
                    </svg>
                    Height
                  </button>
                </div>
              </div>

              {/* Section 3: Transform Coordinate Inputs */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid #3c3c42', paddingTop: '6px' }}>
                <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Transform
                </span>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px 8px' }}>
                  {/* X Coordinate */}
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <span style={{ fontSize: '9px', color: '#8e8e93', width: '8px' }}>X</span>
                    <input
                      type="number"
                      value={
                        activeRing.type === 'image' 
                          ? Math.round(100 - activeRing.width / 2 + activeRing.offsetX)
                          : Math.round(100 + activeRing.offsetX - activeRing.outerRadiusX)
                      }
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        if (activeRing.type === 'image') {
                          updateSelectedRing('offsetX', val - 100 + activeRing.width / 2);
                        } else {
                          updateSelectedRing('offsetX', val - 100 + activeRing.outerRadiusX);
                        }
                      }}
                      style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '2px 4px', color: '#fff', fontSize: '11px', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }}
                    />
                  </div>
                  {/* Y Coordinate */}
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <span style={{ fontSize: '9px', color: '#8e8e93', width: '8px' }}>Y</span>
                    <input
                      type="number"
                      value={
                        activeRing.type === 'image' 
                          ? Math.round(100 - activeRing.height / 2 + activeRing.offsetY)
                          : Math.round(100 + activeRing.offsetY - activeRing.outerRadiusY)
                      }
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        if (activeRing.type === 'image') {
                          updateSelectedRing('offsetY', val - 100 + activeRing.height / 2);
                        } else {
                          updateSelectedRing('offsetY', val - 100 + activeRing.outerRadiusY);
                        }
                      }}
                      style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '2px 4px', color: '#fff', fontSize: '11px', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }}
                    />
                  </div>
                  {/* Width Coordinate */}
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <span style={{ fontSize: '9px', color: '#8e8e93', width: '8px' }}>W</span>
                    <input
                      type="number"
                      value={
                        activeRing.type === 'image' 
                          ? activeRing.width 
                          : activeRing.outerRadiusX * 2
                      }
                      onChange={(e) => {
                        const val = Math.max(1, parseInt(e.target.value) || 0);
                        if (activeRing.type === 'image') {
                          updateSelectedRing('width', val);
                        } else {
                          updateSelectedRing('outerRadiusX', Math.round(val / 2));
                        }
                      }}
                      style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '2px 4px', color: '#fff', fontSize: '11px', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }}
                    />
                  </div>
                  {/* Height Coordinate */}
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <span style={{ fontSize: '9px', color: '#8e8e93', width: '8px' }}>H</span>
                    <input
                      type="number"
                      value={
                        activeRing.type === 'image' 
                          ? activeRing.height 
                          : activeRing.outerRadiusY * 2
                      }
                      onChange={(e) => {
                        const val = Math.max(1, parseInt(e.target.value) || 0);
                        if (activeRing.type === 'image') {
                          updateSelectedRing('height', val);
                        } else {
                          updateSelectedRing('outerRadiusY', Math.round(val / 2));
                        }
                      }}
                      style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '2px 4px', color: '#fff', fontSize: '11px', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* AI DESIGN COPILOT PANEL (docked directly to the right of the toolbox) */}
        {showAiPanel && hasGeminiKey && (
          <div style={{
            height: '100%',
            maxHeight: '100%',
            minHeight: 0,
            minWidth: 0,
            overflow: 'hidden',
            display: 'flex'
          }}>
            <AiDesignerChatPanel
              mode="face"
              currentState={{
                metal,
                faceSide,
                rings,
                upperText,
                lowerText,
                textFont,
                textSize,
                textRadius,
                textKerning,
                symbol,
                textGrayscale,
                depth,
                edgeStyle
              }}
              availableAssets={studioAssets}
              onApplyMutations={handleApplyAiFaceMutations}
              onClose={() => setShowAiPanel(false)}
              tenantConfig={tenantConfig}
              user={user}
              token={token}
            />
          </div>
        )}

        {/* 3. CENTER CANVAS AREA */}
        <div 
          data-testid="face-center-canvas-area"
          onDragOver={(e) => {
            e.preventDefault();
            setIsCanvasDragOver(true);
          }}
          onDragLeave={(e) => {
            if (e.currentTarget === e.target) {
              setIsCanvasDragOver(false);
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            setIsCanvasDragOver(false);
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
              handleUploadAssetFiles(e.dataTransfer.files, { autoAddToCanvas: true });
            }
          }}
          style={{
            flex: 1,
            backgroundColor: '#141416',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            overflow: 'hidden',
            position: 'relative'
          }}
          onClick={() => {
            setSelectedRingId(null);
            if (activeTool !== 'image' && activeTool !== 'text') {
              setActiveTool('select');
            }
          }}
        >
          {isCanvasDragOver && (
            <div 
              data-testid="canvas-drag-overlay"
              style={{
                position: 'absolute',
                inset: '16px',
                border: '2px dashed #1677ff',
                borderRadius: '12px',
                backgroundColor: 'rgba(22, 119, 255, 0.16)',
                backdropFilter: 'blur(3px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 999,
                pointerEvents: 'none',
                gap: '10px'
              }}
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#ffffff' }}>
                Drop image or vector SVG to upload &amp; add to coin face
              </span>
            </div>
          )}
          {/* Square Checkerboard Canvas */}
          <div style={{
            width: `${canvasSize}px`,
            height: `${canvasSize}px`,
            border: '1px solid #3c3c42',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            backgroundImage: 'conic-gradient(#2b2b2f 25%, transparent 25% 50%, #2b2b2f 50% 75%, transparent 75%)',
            backgroundSize: '24px 24px',
            backgroundColor: '#1a1a1c',
            boxShadow: 'none',
            overflow: 'hidden'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div className="coin-3d-renderer" onClick={triggerFastSpin} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div 
                className={`coin-sphere ${isSpinning ? 'spinning-fast' : ''}`}
                style={{
                  filter: 'none',
                  transition: 'none',
                  width: '95%',
                  height: '95%',
                  aspectRatio: '1 / 1'
                }}
              >
                <svg 
                  id="studio-coin-svg"
                  viewBox="0 0 200 200" 
                  width="100%" 
                  height="100%"
                  shapeRendering="geometricPrecision"
                  textRendering="geometricPrecision"
                  style={{ overflow: 'visible' }}
                >
                  <defs>
                    {/* Render paths for all curved text layers */}
                    {[...safeRings].filter(r => r && (r.id === 'typography-layer' || r.isCurved)).map(ring => {
                      const rawR = ring.id === 'typography-layer' ? textRadius : (ring.textRadius || textRadius);
                      const radius = Number(rawR) > 0 ? Number(rawR) : 75;
                      return (
                        <React.Fragment key={`paths-${ring.id}`}>
                          <path id={`topArcPath-${ring.id}`} d={`M ${100 - radius} 100 A ${radius} ${radius} 0 0 1 ${100 + radius} 100`} fill="none" />
                          <path id={`bottomArcPath-${ring.id}`} d={`M ${100 + radius} 100 A ${radius} ${radius} 0 0 1 ${100 - radius} 100`} fill="none" />
                          <path id={`fullCirclePath-${ring.id}`} d={`M 100 ${100 + radius} A ${radius} ${radius} 0 1 1 100.01 ${100 + radius}`} fill="none" />
                        </React.Fragment>
                      );
                    })}

                    <clipPath id="centerClip">
                      <circle cx="100" cy="100" r="41" />
                    </clipPath>
                    
                    {/* Render filters for all curved text layers */}
                    {[...safeRings].filter(r => r && (r.id === 'typography-layer' || r.isCurved)).map(ring => {
                      const textLayer = ring;
                      const hasTextBlur = textLayer && textLayer.enableBlur === true && textLayer.gaussianBlur > 0;
                      
                      // Relief / Shadow configuration
                      let dx = depth === 'low' ? 0.5 : depth === 'high' ? 2 : 1;
                      let dy = depth === 'low' ? 0.5 : depth === 'high' ? 2 : 1;
                      let stdDevShadow = depth === 'low' ? 0.5 : depth === 'high' ? 1.5 : 1;
                      let opacity = depth === 'low' ? 0.5 : depth === 'high' ? 0.8 : 0.6;
                      let color = 'black';

                      if (textLayer && textLayer.shadowEnabled) {
                        dx = textLayer.shadowOffsetX !== undefined ? textLayer.shadowOffsetX : 2;
                        dy = textLayer.shadowOffsetY !== undefined ? textLayer.shadowOffsetY : 2;
                        stdDevShadow = textLayer.shadowBlur !== undefined ? textLayer.shadowBlur : 3;
                        opacity = textLayer.shadowOpacity !== undefined ? textLayer.shadowOpacity : 0.5;
                        color = textLayer.shadowColor || '#000000';
                      }

                      return (
                        <filter key={`filter-${ring.id}`} id={`typography-relief-filter-${ring.id}`} x="-20%" y="-20%" width="140%" height="140%">
                          {/* 1. Gaussian Blur (if enabled) */}
                          {hasTextBlur && (
                            <feGaussianBlur stdDeviation={textLayer.gaussianBlur} result="blurred" />
                          )}
                          
                          {/* 2. Drop Shadow relief effect */}
                          <feDropShadow 
                            in={hasTextBlur ? "blurred" : "SourceGraphic"} 
                            dx={dx} 
                            dy={dy} 
                            stdDeviation={stdDevShadow} 
                            floodColor={color} 
                            floodOpacity={opacity} 
                          />
                        </filter>
                      );
                    })}
                    
                    {/* Dynamic filters and masks for each ring */}
                    {safeRings.filter(r => r && r.visible !== false).map(ring => {
                      if (ring.type === 'image') {
                        const rw = Number(ring.width) > 0 ? Number(ring.width) : 80;
                        const rh = Number(ring.height) > 0 ? Number(ring.height) : 80;
                        const offX = Number(ring.offsetX) || 0;
                        const offY = Number(ring.offsetY) || 0;
                        const rot = Number(ring.angle) || 0;
                        return (
                          <React.Fragment key={`defs-${ring.id}`}>
                            <clipPath id={`clip-${ring.id}`}>
                              {ring.cropShape === 'rect' ? (
                                <rect 
                                  x={100 - rw / 2 + offX} 
                                  y={100 - rh / 2 + offY} 
                                  width={rw} 
                                  height={rh} 
                                  rx={Number(ring.borderRadius) || 0}
                                  transform={rot ? `rotate(${rot}, ${100 + offX}, ${100 + offY})` : undefined}
                                />
                              ) : (
                                <ellipse 
                                  cx={100 + offX} 
                                  cy={100 + offY} 
                                  rx={rw / 2} 
                                  ry={rh / 2} 
                                  transform={rot ? `rotate(${rot}, ${100 + offX}, ${100 + offY})` : undefined}
                                />
                              )}
                            </clipPath>
                            {((ring.enableBlur === true && ring.gaussianBlur > 0) || ring.invertColors || ring.isGrayscale || ring.imageGrayscale || ring.grayscale === true || ring.monochrome) && (
                              <filter id={`filter-${ring.id}`}>
                                {ring.enableBlur === true && ring.gaussianBlur > 0 && (
                                  <feGaussianBlur stdDeviation={ring.gaussianBlur} result="blurred" />
                                )}
                                {(ring.isGrayscale || ring.imageGrayscale || ring.grayscale === true || ring.monochrome) && (
                                  <feColorMatrix 
                                    type="matrix" 
                                    values="0.2126 0.7152 0.0722 0 0  0.2126 0.7152 0.0722 0 0  0.2126 0.7152 0.0722 0 0  0 0 0 1 0" 
                                    result="grayscale"
                                  />
                                )}
                                {ring.monochrome && (() => {
                                  const contrastVal = ring.monochromeContrast !== undefined ? ring.monochromeContrast : (ring.contrast !== undefined ? ring.contrast : 400);
                                  const threshVal = ring.monochromeThreshold !== undefined ? ring.monochromeThreshold : 50;
                                  const slope = contrastVal / 100;
                                  const intercept = -slope * (threshVal / 100) + 0.5;
                                  return (
                                    <feComponentTransfer result="monochrome">
                                      <feFuncR type="linear" slope={slope} intercept={intercept} />
                                      <feFuncG type="linear" slope={slope} intercept={intercept} />
                                      <feFuncB type="linear" slope={slope} intercept={intercept} />
                                    </feComponentTransfer>
                                  );
                                })()}
                                {ring.invertColors && (
                                  <feColorMatrix 
                                    type="matrix" 
                                    values="-1 0 0 0 1  0 -1 0 0 1  0 0 -1 0 1  0 0 0 1 0" 
                                  />
                                )}
                              </filter>
                            )}
                            {ring.shadowEnabled && (
                              <filter id={`shadow-${ring.id}`} x="-20%" y="-20%" width="140%" height="140%">
                                <feDropShadow 
                                  dx={ring.shadowOffsetX !== undefined ? ring.shadowOffsetX : 2} 
                                  dy={ring.shadowOffsetY !== undefined ? ring.shadowOffsetY : 2} 
                                  stdDeviation={ring.shadowBlur !== undefined ? ring.shadowBlur : 3} 
                                  floodColor={ring.shadowColor || '#000000'} 
                                  floodOpacity={ring.shadowOpacity !== undefined ? ring.shadowOpacity : 0.5} 
                                />
                              </filter>
                            )}
                          </React.Fragment>
                        );
                      }
                      const hasPattern = !!ring.patternUrl;
                      return (
                        <React.Fragment key={`defs-${ring.id}`}>
                          {/* Blur filters */}
                          {ring.enableBlur === true && ring.outerBlur > 0 && (
                            <filter id={`blur-out-${ring.id}`}>
                              <feGaussianBlur stdDeviation={ring.outerBlur} />
                            </filter>
                          )}
                          {ring.enableBlur === true && ring.innerBlur > 0 && (
                            <filter id={`blur-in-${ring.id}`}>
                              <feGaussianBlur stdDeviation={ring.innerBlur} />
                            </filter>
                          )}
                          {ring.shadowEnabled && (
                            <filter id={`shadow-${ring.id}`} x="-20%" y="-20%" width="140%" height="140%">
                              <feDropShadow 
                                dx={ring.shadowOffsetX !== undefined ? ring.shadowOffsetX : 2} 
                                dy={ring.shadowOffsetY !== undefined ? ring.shadowOffsetY : 2} 
                                stdDeviation={ring.shadowBlur !== undefined ? ring.shadowBlur : 3} 
                                floodColor={ring.shadowColor || '#000000'} 
                                floodOpacity={ring.shadowOpacity !== undefined ? ring.shadowOpacity : 0.5} 
                              />
                            </filter>
                          )}

                          {/* Noise composite filter */}
                          <filter id={`noise-filter-${ring.id}`}>
                            <feTurbulence 
                              type="fractalNoise" 
                              baseFrequency={1.5 - ((ring.noiseSize || 50) / 100) * 1.45} 
                              numOctaves="3" 
                              result="rawNoise" 
                            />
                            <feColorMatrix type="matrix" values="0.33 0.33 0.33 0 0  0.33 0.33 0.33 0 0  0.33 0.33 0.33 0 0  0 0 0 1 0" result="monoNoise" />
                            <feComposite operator="arithmetic" k1="0" k2="1" k3={((ring.noise || 0) / 150) || 0} k4={(-(ring.noise || 0) / 300) || 0} in="SourceGraphic" in2="monoNoise" />
                          </filter>

                          {/* Custom pattern */}
                          {hasPattern && (
                            <pattern id={`pattern-${ring.id}`} patternUnits="userSpaceOnUse" x="0" y="0" width="80" height="80">
                              <image 
                                href={ring.patternUrl} 
                                x="0" 
                                y="0" 
                                width="80" 
                                height="80" 
                                preserveAspectRatio="xMidYMid slice"
                                style={{ filter: 'grayscale(100%)' }}
                              />
                            </pattern>
                          )}

                          {/* Mask defining the outer & inner boundary */}
                          <mask id={`mask-${ring.id}`}>
                            {/* Base black background */}
                            <rect x="-200" y="-200" width="600" height="600" fill="black" />
                            {/* Outer white ellipse */}
                            <ellipse 
                              cx={100 + (ring.offsetX || 0)} 
                              cy={100 + (ring.offsetY || 0)} 
                              rx={ring.outerRadiusX || 0} 
                              ry={ring.outerRadiusY || 0} 
                              fill="white" 
                              filter={ring.enableBlur === true && ring.outerBlur > 0 ? `url(#blur-out-${ring.id})` : undefined} 
                            />
                            {/* Inner black ellipse */}
                            {ring.innerRadiusX > 0 && ring.innerRadiusY > 0 && (
                              <ellipse 
                                cx={100 + (ring.offsetX || 0)} 
                                cy={100 + (ring.offsetY || 0)} 
                                rx={ring.innerRadiusX || 0} 
                                ry={ring.innerRadiusY || 0} 
                                fill="black" 
                                filter={ring.enableBlur === true && ring.innerBlur > 0 ? `url(#blur-in-${ring.id})` : undefined} 
                              />
                            )}
                          </mask>
                        </React.Fragment>
                      );
                    })}
                  </defs>

                  {/* Coin Edge background layer */}
                  <circle cx="100" cy="100" r="97" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />

                  {/* Render rings in reverse stack order */}
                  {[...safeRings].filter(r => r && r.type !== 'group' && isLayerVisibleInSVG(r)).reverse().map(ring => {
                    if (ring.type === 'image') {
                      return (
                        <g 
                          key={`group-${ring.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTool('select');
                            setSelectedRingId(ring.id);
                            setActiveRightTab('properties');
                          }}
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            setActiveTool('select');
                            setSelectedRingId(ring.id);
                            setActiveRightTab('properties');
                            if (!ring.locked) {
                              handleBoundingBoxPointerDown(e, ring, 'drag');
                            }
                          }}
                          style={{ cursor: ring.locked ? 'default' : 'move', mixBlendMode: ring.mixBlendMode || 'normal' }}
                          filter={ring.shadowEnabled ? `url(#shadow-${ring.id})` : undefined}
                        >
                          {(() => {
                            const rw = Number(ring.width) > 0 ? Number(ring.width) : 80;
                            const rh = Number(ring.height) > 0 ? Number(ring.height) : 80;
                            const offX = Number(ring.offsetX) || 0;
                            const offY = Number(ring.offsetY) || 0;
                            const zoom = Math.max(0.1, Number(ring.imgZoom) || 1);
                            const panX = ((Number(ring.imgPanX) || 0) / 100) * rw;
                            const panY = ((Number(ring.imgPanY) || 0) / 100) * rh;

                            // Calculate base dimensions using natural aspect ratio (object-fit: cover math matching crop modal)
                            const frameAspect = rw / rh;
                            const imgAspect = Number(ring.imgAspect) > 0
                              ? Number(ring.imgAspect)
                              : (Number(ring.naturalWidth) > 0 && Number(ring.naturalHeight) > 0 ? Number(ring.naturalWidth) / Number(ring.naturalHeight) : frameAspect);

                            let baseW = rw;
                            let baseH = rh;
                            if (imgAspect > frameAspect) {
                              baseW = rh * imgAspect;
                              baseH = rh;
                            } else {
                              baseW = rw;
                              baseH = rw / (imgAspect || 1);
                            }

                            const imgW = baseW * zoom;
                            const imgH = baseH * zoom;
                            const imgX = 100 - imgW / 2 + offX + panX;
                            const imgY = 100 - imgH / 2 + offY + panY;

                            const cx = 100 + offX;
                            const cy = 100 + offY;
                            const transforms = [];
                            if (ring.angle) transforms.push(`rotate(${Number(ring.angle) || 0}, ${cx}, ${cy})`);
                            if (ring.flipH || ring.flipV) {
                              transforms.push(`translate(${cx}, ${cy}) scale(${ring.flipH ? -1 : 1}, ${ring.flipV ? -1 : 1}) translate(${-cx}, ${-cy})`);
                            }
                            const isImageGrayscale = ring.isGrayscale || ring.imageGrayscale || ring.grayscale === true;
                            const isImageMono = !!ring.monochrome;
                            const hasFilter = (ring.enableBlur === true && ring.gaussianBlur > 0) || ring.invertColors || isImageGrayscale || isImageMono;

                            const cssFilters = [];
                            if (isImageGrayscale || isImageMono) {
                              cssFilters.push('grayscale(100%)');
                            }
                            if (isImageMono) {
                              const contrastVal = ring.monochromeContrast !== undefined ? ring.monochromeContrast : (ring.contrast !== undefined ? ring.contrast : 400);
                              cssFilters.push(`contrast(${contrastVal}%)`);
                              const threshVal = ring.monochromeThreshold !== undefined ? ring.monochromeThreshold : 50;
                              if (threshVal !== 50) {
                                const brightnessVal = Math.round(100 + (50 - threshVal) * 1.5);
                                cssFilters.push(`brightness(${brightnessVal}%)`);
                              }
                            }
                            if (ring.invertColors) {
                              cssFilters.push('invert(100%)');
                            }

                            return (
                              <image
                                href={ring.imageUrl}
                                x={imgX}
                                y={imgY}
                                width={imgW}
                                height={imgH}
                                preserveAspectRatio="none"
                                clipPath={`url(#clip-${ring.id})`}
                                transform={transforms.length > 0 ? transforms.join(' ') : undefined}
                                filter={hasFilter ? `url(#filter-${ring.id})` : undefined}
                                style={{
                                  opacity: (ring.opacity !== undefined && ring.opacity !== null && !isNaN(ring.opacity)) ? ring.opacity / 100 : 1,
                                  filter: cssFilters.length > 0 ? cssFilters.join(' ') : undefined
                                }}
                              />
                            );
                          })()}
                        </g>
                      );
                    }

                    if (ring.id === 'typography-layer' || ring.isCurved) {
                      const textLayer = ring;
                      const hasFilter = textLayer && (
                        (textLayer.enableBlur === true && textLayer.gaussianBlur > 0) || 
                        textLayer.shadowEnabled
                      );
                      const filterStyle = hasFilter ? `url(#typography-relief-filter-${ring.id})` : undefined;

                      const isPrimary = ring.id === 'typography-layer';
                      const mode = isPrimary ? textMode : (ring.textMode || 'single');
                      const font = isPrimary ? textFont : (ring.fontFamily || '"Arial Black", sans-serif');
                      const size = isPrimary ? textSize : (ring.fontSize || 16);
                      const weight = isPrimary ? textFontWeight : (ring.fontWeight || '900');
                      const fontStyle = isPrimary ? textFontStyle : (ring.fontStyle || 'normal');
                      const kerning = isPrimary ? textKerning : (ring.letterSpacing !== undefined ? ring.letterSpacing : 1.2);
                      const caseMode = isPrimary ? textCase : (ring.textCase || 'uppercase');
                      
                      const grayscale = isPrimary ? textGrayscale : (ring.grayscale !== undefined ? ring.grayscale : 100);
                      const brightness = 255 - Math.round((grayscale / 100) * 255);
                      const fillCol = `rgb(${brightness}, ${brightness}, ${brightness})`;

                      const singleTxt = isPrimary ? singleText : (ring.singleText || '');
                      const upperTxt = isPrimary ? upperText : (ring.upperText || '');
                      const lowerTxt = isPrimary ? lowerText : (ring.lowerText || '');
                      const offX = Number(ring.offsetX) || 0;
                      const offY = Number(ring.offsetY) || 0;

                      return (
                        <g
                          key={`group-${ring.id}`}
                          transform={(offX || offY) ? `translate(${offX}, ${offY})` : undefined}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTool('select');
                            setSelectedRingId(ring.id);
                            setActiveRightTab('properties');
                          }}
                          style={{ cursor: 'pointer', mixBlendMode: ring.mixBlendMode || 'normal' }}
                        >
                          {mode === 'single' ? (
                            <text 
                              fontFamily={font} 
                              fontSize={size} 
                              fontWeight={weight} 
                              fontStyle={fontStyle}
                              letterSpacing={kerning} 
                              style={{ filter: filterStyle }}
                            >
                              <textPath href={`#fullCirclePath-${ring.id}`} startOffset="50%" textAnchor="middle" fill={fillCol}>
                                {formatTextCase(singleTxt, caseMode)}
                              </textPath>
                            </text>
                          ) : (
                            <>
                              <text 
                                fontFamily={font} 
                                fontSize={size} 
                                fontWeight={weight} 
                                fontStyle={fontStyle}
                                letterSpacing={kerning} 
                                style={{ filter: filterStyle }}
                              >
                                <textPath href={`#topArcPath-${ring.id}`} startOffset="50%" textAnchor="middle" fill={fillCol}>
                                  {formatTextCase(upperTxt, caseMode)}
                                </textPath>
                              </text>

                              <text 
                                fontFamily={font} 
                                fontSize={size} 
                                fontWeight={weight} 
                                fontStyle={fontStyle}
                                letterSpacing={kerning} 
                                style={{ filter: filterStyle }}
                              >
                                <textPath href={`#bottomArcPath-${ring.id}`} startOffset="50%" textAnchor="middle" fill={fillCol}>
                                  {formatTextCase(lowerTxt, caseMode)}
                                </textPath>
                              </text>
                            </>
                          )}
                        </g>
                      );
                    }

                    if (ring.type === 'text') {
                      const textBrightness = 255 - Math.round(((ring.grayscale !== undefined ? ring.grayscale : textGrayscale) / 100) * 255);
                      const textFillColor = `rgb(${textBrightness}, ${textBrightness}, ${textBrightness})`;
                      
                      const rotateStr = ring.shear ? `rotate(${ring.shear}, ${100 + ring.offsetX}, ${100 + ring.offsetY})` : '';
                      const scaleStr = (ring.hScale !== undefined && ring.hScale !== 100) || (ring.vScale !== undefined && ring.vScale !== 100)
                        ? `scale(${(ring.hScale || 100) / 100}, ${(ring.vScale || 100) / 100})`
                        : '';
                      const transformStr = [rotateStr, scaleStr].filter(Boolean).join(' ') || undefined;

                      return (
                        <g 
                          key={`group-${ring.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTool('select');
                            setSelectedRingId(ring.id);
                            setActiveRightTab('properties');
                          }}
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            setActiveTool('select');
                            setSelectedRingId(ring.id);
                            setActiveRightTab('properties');
                            if (!ring.locked) {
                              handleBoundingBoxPointerDown(e, ring, 'drag');
                            }
                          }}
                          style={{ cursor: ring.locked ? 'default' : 'move', mixBlendMode: ring.mixBlendMode || 'normal' }}
                          filter={ring.shadowEnabled ? `url(#shadow-${ring.id})` : undefined}
                        >
                          <text
                            x={100 + ring.offsetX}
                            y={100 + ring.offsetY}
                            fontFamily={ring.fontFamily || '"Arial Black", sans-serif'}
                            fontSize={ring.fontSize || 16}
                            fontWeight={ring.fontWeight || 'normal'}
                            fontStyle={ring.fontStyle || 'normal'}
                            letterSpacing={ring.letterSpacing || 0}
                            textDecoration={ring.textDecoration || 'none'}
                            textAnchor={ring.textAlign === 'left' ? 'start' : ring.textAlign === 'right' ? 'end' : 'middle'}
                            fill={textFillColor}
                            filter={ring.enableBlur === true && ring.gaussianBlur > 0 ? `url(#blur-${ring.id})` : undefined}
                            transform={transformStr}
                            style={{
                              dominantBaseline: 'central'
                            }}
                          >
                            {formatTextCase(ring.text || '', ring.textCase)}
                          </text>
                        </g>
                      );
                    }

                    const brightness = 255 - Math.round((ring.grayscale / 100) * 255);
                    const fillColor = `rgb(${brightness}, ${brightness}, ${brightness})`;
                    const hasPattern = !!ring.patternUrl;

                    return (
                      <g 
                        key={`group-${ring.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTool('select');
                          setSelectedRingId(ring.id);
                          setActiveRightTab('properties');
                        }}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          setActiveTool('select');
                          setSelectedRingId(ring.id);
                          setActiveRightTab('properties');
                          if (!ring.locked) {
                            handleBoundingBoxPointerDown(e, ring, 'drag');
                          }
                        }}
                        style={{ cursor: ring.locked ? 'default' : 'move', mixBlendMode: ring.mixBlendMode || 'normal' }}
                        filter={ring.shadowEnabled ? `url(#shadow-${ring.id})` : undefined}
                      >
                        <g mask={`url(#mask-${ring.id})`}>
                          <ellipse
                            cx={100 + (Number(ring.offsetX) || 0)}
                            cy={100 + (Number(ring.offsetY) || 0)}
                            rx={(ring.outerRadiusX !== undefined && !isNaN(ring.outerRadiusX)) ? Number(ring.outerRadiusX) : (Number(ring.width) > 0 ? Math.round(Number(ring.width) / 2) : 50)}
                            ry={(ring.outerRadiusY !== undefined && !isNaN(ring.outerRadiusY)) ? Number(ring.outerRadiusY) : (Number(ring.height) > 0 ? Math.round(Number(ring.height) / 2) : 50)}
                            fill={hasPattern ? `url(#pattern-${ring.id})` : fillColor}
                            filter={ring.noise > 0 ? `url(#noise-filter-${ring.id})` : undefined}
                          />
                          {hasPattern && (
                            <ellipse
                              cx={100 + (Number(ring.offsetX) || 0)}
                              cy={100 + (Number(ring.offsetY) || 0)}
                              rx={(ring.outerRadiusX !== undefined && !isNaN(ring.outerRadiusX)) ? Number(ring.outerRadiusX) : (Number(ring.width) > 0 ? Math.round(Number(ring.width) / 2) : 50)}
                              ry={(ring.outerRadiusY !== undefined && !isNaN(ring.outerRadiusY)) ? Number(ring.outerRadiusY) : (Number(ring.height) > 0 ? Math.round(Number(ring.height) / 2) : 50)}
                              fill={fillColor}
                              style={{ mixBlendMode: 'multiply', opacity: 0.5 }}
                            />
                          )}
                        </g>
                      </g>
                    );
                  })}

                  {/* Draw rectangular bounding box and 8 handles for the active ring (matching Card Template Designer) */}
                  {safeRings.filter(r => r && r.visible !== false).map(ring => {
                    if (selectedRingId !== ring.id) return null;
                    const box = getRingBoundingBox(ring, textRadius);
                    if (!box) return null;
                    const handles = getHandles(box);
                    const handleSize = 3.2;

                    return (
                      <g
                        key={`selection-${ring.id}`}
                        className="selection-outline"
                        transform={box.transform}
                      >
                        {/* 1.5px solid #1677ff bounding box */}
                        <rect
                          data-testid="selection-bounding-box"
                          className="selection-outline"
                          x={box.x}
                          y={box.y}
                          width={box.width}
                          height={box.height}
                          fill="none"
                          stroke="#1677ff"
                          strokeWidth="1.5"
                          vectorEffect="non-scaling-stroke"
                          style={{
                            cursor: ring.locked ? 'default' : 'move',
                            pointerEvents: ring.locked ? 'none' : 'stroke'
                          }}
                          onPointerDown={(e) => handleBoundingBoxPointerDown(e, ring, 'drag')}
                        />

                        {/* 8 control handles: nw, n, ne, e, se, s, sw, w (white fill, 1.5px #1677ff border) */}
                        {!ring.locked && handles.map(h => (
                          <rect
                            key={h.id}
                            data-testid={`selection-handle-${h.id}`}
                            className="selection-outline"
                            x={h.x - handleSize / 2}
                            y={h.y - handleSize / 2}
                            width={handleSize}
                            height={handleSize}
                            fill="#ffffff"
                            stroke="#1677ff"
                            strokeWidth="1.5"
                            vectorEffect="non-scaling-stroke"
                            style={{
                              cursor: h.cursor,
                              pointerEvents: 'auto'
                            }}
                            onPointerDown={(e) => handleBoundingBoxPointerDown(e, ring, 'resize', h.id)}
                          />
                        ))}

                        {/* On-canvas Pan & Zoom Image Action Button for Image Layers */}
                        {ring.type === 'image' && ring.imageUrl && !ring.locked && (
                          <g
                            data-testid="pan-zoom-crop-btn"
                            className="selection-outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              openImageAdjustModal(ring);
                            }}
                            style={{ cursor: 'pointer', pointerEvents: 'all' }}
                            transform={`translate(${box.cx}, ${box.y + box.height + 6.5})`}
                          >
                            <rect
                              x="-50"
                              y="-6"
                              width="100"
                              height="12"
                              rx="6"
                              fill="#1677ff"
                              stroke="#ffffff"
                              strokeWidth="0.6"
                              vectorEffect="non-scaling-stroke"
                            />
                            <text
                              x="0"
                              y="2.6"
                              fill="#ffffff"
                              fontSize="5.2"
                              fontWeight="600"
                              textAnchor="middle"
                              style={{ pointerEvents: 'none', userSelect: 'none' }}
                            >
                              Pan &amp; Zoom Image
                            </text>
                          </g>
                        )}
                      </g>
                    );
                  })}



                  {/* Center emblem or custom graphic symbol */}
                  {symbol && (symbol.startsWith('http') || symbol.includes('.') || symbol.includes('/')) ? (
                    <image 
                      href={symbol} 
                      x="59" 
                      y="59" 
                      width="82" 
                      height="82"
                      clipPath="url(#centerClip)"
                      style={{
                        filter: currentRelief.filter,
                        userSelect: 'none',
                        pointerEvents: 'none'
                      }}
                    />
                  ) : symbol ? (
                    <text 
                      x="100" 
                      y="114" 
                      fontSize="46" 
                      textAnchor="middle" 
                      fill={textColor}
                      style={{ 
                        filter: currentRelief.filter,
                        userSelect: 'none',
                        pointerEvents: 'none'
                      }}
                    >
                      {symbol}
                    </text>
                  ) : null}
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* 4. RIGHT SIDEBAR PANEL */}
        <div style={{
          backgroundColor: '#2b2b2f',
          borderLeft: '1px solid #3c3c42',
          display: 'flex',
          flexDirection: 'column',
          userSelect: 'none',
          overflow: 'hidden',
          width: '414px'
        }}>
          {/* Tab Selector */}
          <div style={{
            display: 'flex',
            height: '36px',
            backgroundColor: '#232326',
            borderBottom: '1px solid #3c3c42',
            padding: '0 4px',
            gap: '2px'
          }}>
            {[
              { id: 'layers', label: 'Layers' },
              { id: 'properties', label: 'Properties' },
              { id: 'materials', label: 'Materials' },
              { id: 'slicer', label: 'Slicer' }
            ].map(t => (
              <button
                key={t.id}
                data-testid={`right-tab-${t.id}`}
                type="button"
                onClick={() => setActiveRightTab(t.id)}
                style={{
                  flex: 1,
                  height: '100%',
                  backgroundColor: (activeRightTab === t.id || (t.id === 'slicer' && activeRightTab === 'settings')) ? '#2b2b2f' : 'transparent',
                  color: (activeRightTab === t.id || (t.id === 'slicer' && activeRightTab === 'settings')) ? '#ffffff' : '#8e8e93',
                  border: 'none',
                  borderBottom: (activeRightTab === t.id || (t.id === 'slicer' && activeRightTab === 'settings')) ? '2px solid #ffffff' : 'none',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  padding: '0'
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab Contents */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            
            {/* LAYERS TAB */}
            {activeRightTab === 'layers' && (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '11px',
                  color: '#a2a2a8',
                  padding: '8px 12px',
                  backgroundColor: '#2b2b2f',
                  borderBottom: '1px solid #232326',
                  gap: '6px'
                }}>
                  {/* Opacity */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', position: 'relative' }}>
                    <span>Opacity:</span>
                    <button
                      type="button"
                      onClick={() => setShowOpacitySlider(!showOpacitySlider)}
                      disabled={!selectedRingId}
                      style={{
                        backgroundColor: '#1b1b1d',
                        border: '1px solid #3c3c42',
                        borderRadius: '3px',
                        color: '#fff',
                        fontSize: '10px',
                        padding: '1px 6px',
                        cursor: selectedRingId ? 'pointer' : 'default',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}
                    >
                      {activeRing ? (activeRing.opacity !== undefined ? activeRing.opacity : 100) : 100}% ▾
                    </button>

                    {showOpacitySlider && selectedRingId && (
                      <>
                        <div
                          onClick={() => setShowOpacitySlider(false)}
                          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
                        />
                        <div style={{
                          position: 'absolute',
                          top: '20px',
                          left: '32px',
                          backgroundColor: '#1b1b1d',
                          border: '1px solid #3c3c42',
                          borderRadius: '4px',
                          padding: '8px',
                          zIndex: 100,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                          gap: '6px'
                        }}>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={activeRing ? (activeRing.opacity !== undefined ? activeRing.opacity : 100) : 100}
                            onMouseDown={saveHistoryState}
                            onChange={(evt) => {
                              if (selectedRingId && activeRing) {
                                updateSelectedRing('opacity', parseInt(evt.target.value));
                              }
                            }}
                            style={{
                              height: '80px',
                              width: '20px',
                              WebkitAppearance: 'slider-vertical',
                              cursor: 'pointer',
                              outline: 'none'
                            }}
                          />
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={activeRing ? (activeRing.opacity !== undefined ? activeRing.opacity : 100) : 100}
                            onChange={(evt) => {
                              let val = parseInt(evt.target.value);
                              if (isNaN(val)) val = 0;
                              if (val < 0) val = 0;
                              if (val > 100) val = 100;
                              if (selectedRingId && activeRing) {
                                updateSelectedRing('opacity', val);
                              }
                            }}
                            style={{
                              width: '32px',
                              backgroundColor: '#222225',
                              border: '1px solid #2d2d30',
                              borderRadius: '3px',
                              color: '#fff',
                              fontSize: '9px',
                              padding: '1px 2px',
                              textAlign: 'center',
                              outline: 'none'
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* Blend Mode */}
                  <select
                    value={activeRing ? (activeRing.mixBlendMode || 'normal') : 'normal'}
                    onChange={(e) => {
                      if (selectedRingId && activeRing) {
                        updateSelectedRingWithHistory('mixBlendMode', e.target.value);
                      }
                    }}
                    disabled={!selectedRingId}
                    style={{
                      backgroundColor: '#1b1b1d',
                      border: '1px solid #3c3c42',
                      borderRadius: '3px',
                      color: '#fff',
                      fontSize: '10px',
                      padding: '1px 4px',
                      flexGrow: 1,
                      outline: 'none',
                      cursor: selectedRingId ? 'pointer' : 'default'
                    }}
                  >
                    <option value="normal">Normal</option>
                    <option value="multiply">Multiply</option>
                    <option value="screen">Screen</option>
                    <option value="overlay">Overlay</option>
                    <option value="darken">Darken</option>
                    <option value="lighten">Lighten</option>
                    <option value="color-dodge">Color Dodge</option>
                    <option value="color-burn">Color Burn</option>
                    <option value="difference">Difference</option>
                    <option value="exclusion">Exclusion</option>
                    <option value="hue">Hue</option>
                    <option value="saturation">Saturation</option>
                    <option value="color">Color</option>
                    <option value="luminosity">Luminosity</option>
                  </select>

                  {/* Settings & Lock */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span 
                      onClick={() => {
                        if (selectedRingId) {
                          setActiveRightTab('properties');
                        }
                      }}
                      style={{ display: 'inline-flex', alignItems: 'center', cursor: selectedRingId ? 'pointer' : 'default', opacity: selectedRingId ? 0.8 : 0.4 }} 
                      title="Properties Settings"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                      </svg>
                    </span>
                    <span 
                      onClick={() => {
                        if (selectedRingId && activeRing) {
                          updateSelectedRingWithHistory('locked', !activeRing.locked);
                        }
                      }}
                      style={{ display: 'inline-flex', alignItems: 'center', cursor: selectedRingId ? 'pointer' : 'default', opacity: selectedRingId && activeRing?.locked ? 1 : 0.4 }}
                      title="Lock Layer"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </span>
                  </div>
                </div>

                <div 
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleContainerDrop}
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#232326'
                  }}
                >
                  {safeRings.map((ring, idx) => {
                    if (!ring) return null;
                    const isSelected = selectedRingId === ring.id;
                    const isDragged = draggedLayerIndex === idx;

                    // If any parent folder is collapsed, hide this item in the sidebar list
                    if (ring.parentId) {
                      const parent = safeRings.find(el => el && el.id === ring.parentId);
                      if (parent && parent.collapsed) return null;
                    }

                    return (
                      <div
                        key={`layer-${ring.id}`}
                        onClick={() => {
                          setSelectedRingId(ring.id);
                        }}
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDrop={(e) => handleDrop(e, idx)}
                        onDragEnd={handleDragEnd}
                        style={{
                          display: 'flex',
                          height: '36px',
                          alignItems: 'center',
                          borderBottom: '1px solid #1e1e21',
                          cursor: isDragged ? 'grabbing' : 'grab',
                          backgroundColor: isSelected ? '#3a3a40' : isDragged ? 'rgba(255, 255, 255, 0.05)' : '#2b2b2f',
                          color: isSelected ? '#ffffff' : '#dfdfe3',
                          fontSize: '11px',
                          transition: 'background-color 0.1s',
                          paddingLeft: `${getElementDepth(ring) * 16}px`
                        }}
                      >
                        {/* Column 1: Type Icon */}
                        <div style={{
                          width: '32px',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRight: '1px solid #1e1e21',
                          backgroundColor: isSelected ? 'rgba(255,255,255,0.08)' : 'transparent',
                          flexShrink: 0
                        }}>
                          {ring.type === 'group' ? (
                            <span
                              onClick={(evt) => {
                                evt.stopPropagation();
                                toggleGroupCollapse(ring.id);
                              }}
                              style={{ cursor: 'pointer', fontSize: '9px', display: 'flex', alignItems: 'center', gap: '2px', color: '#8e8e93' }}
                            >
                              {ring.collapsed ? '▶' : '▼'}
                            </span>
                          ) : ring.id === 'typography-layer' ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                              <path d="M4 15a8 8 0 0 1 16 0" />
                              <path d="M10 8h4" />
                              <path d="M12 8v5" />
                            </svg>
                          ) : ring.type === 'text' ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                              <polyline points="4 7 4 4 20 4 20 7" />
                              <line x1="9" y1="20" x2="15" y2="20" />
                              <line x1="12" y1="4" x2="12" y2="20" />
                            </svg>
                          ) : ring.type === 'image' ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                              <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                              <line x1="2" y1="20" x2="22" y2="20" />
                              <polyline points="12 13 17 8 22 13" />
                              <polyline points="2 13 7 8 13 13" />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          )}
                        </div>

                        {/* Column 2: Miniature Thumbnail */}
                        <div style={{
                          width: '36px',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRight: '1px solid #1e1e21',
                          flexShrink: 0
                        }}>
                          {ring.type === 'group' ? (
                            <div style={{
                              width: '16px',
                              height: '16px',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              border: '1px solid rgba(255,255,255,0.2)',
                              borderRadius: '2px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#fff'
                            }}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'block' }}>
                                <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
                              </svg>
                            </div>
                          ) : ring.type === 'text' ? (
                            <div style={{
                              width: '16px',
                              height: '16px',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              border: '1px solid rgba(255,255,255,0.2)',
                              borderRadius: '2px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#fff',
                              fontSize: '9px',
                              fontWeight: 'bold',
                              fontFamily: 'system-ui, sans-serif'
                            }}>
                              T
                            </div>
                          ) : ring.type === 'image' ? (
                            <div style={{
                              width: '16px',
                              height: '16px',
                              backgroundColor: '#444',
                              border: '1px solid rgba(255,255,255,0.2)',
                              borderRadius: '2px',
                              overflow: 'hidden'
                            }}>
                              <img
                                src={ring.imageUrl}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'grayscale(1)' }}
                              />
                            </div>
                          ) : (
                            <div style={{
                              width: '16px',
                              height: '16px',
                              backgroundColor: `rgb(${255 - Math.round((ring.grayscale / 100) * 255)}, ${255 - Math.round((ring.grayscale / 100) * 255)}, ${255 - Math.round((ring.grayscale / 100) * 255)})`,
                              border: '1px solid rgba(255,255,255,0.3)',
                              borderRadius: ring.constrainOuter ? '50%' : '2px'
                            }} />
                          )}
                        </div>

                        {/* Column 3: Name / Desc */}
                        <div 
                          onDoubleClick={(evt) => {
                            evt.stopPropagation();
                            setRenamingLayerId(ring.id);
                            setRenameValue(ring.name || '');
                          }}
                          style={{
                            flex: 1,
                            paddingLeft: '8px',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            overflow: 'hidden'
                          }}
                        >
                          {renamingLayerId === ring.id ? (
                            <input
                              type="text"
                              value={renameValue}
                              onChange={(evt) => setRenameValue(evt.target.value)}
                              onKeyDown={(evt) => {
                                if (evt.key === 'Enter') {
                                  evt.stopPropagation();
                                  setRings(rings.map(r => r.id === ring.id ? { ...r, name: renameValue } : r));
                                  setRenamingLayerId(null);
                                } else if (evt.key === 'Escape') {
                                  evt.stopPropagation();
                                  setRenamingLayerId(null);
                                }
                              }}
                              onBlur={() => {
                                setRings(rings.map(r => r.id === ring.id ? { ...r, name: renameValue } : r));
                                  setRenamingLayerId(null);
                              }}
                              autoFocus
                              style={{
                                backgroundColor: '#141416',
                                border: '1px solid #1677ff',
                                borderRadius: '3px',
                                padding: '2px 4px',
                                color: '#fff',
                                fontSize: '11px',
                                outline: 'none',
                                width: '100%',
                                boxSizing: 'border-box'
                              }}
                            />
                          ) : (
                            <span style={{
                              fontWeight: isSelected ? '700' : 'normal',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              flex: 1,
                              fontSize: '11px',
                              color: isSelected ? '#ffffff' : '#dfdfe3'
                            }}>
                              {ring.name}
                            </span>
                          )}
                        </div>

                        {/* Column 4: Lock Toggle */}
                        <div
                          onClick={(evt) => {
                            evt.stopPropagation();
                            setRings(rings.map(r => r.id === ring.id ? { ...r, locked: !r.locked } : r));
                          }}
                          style={{
                            width: '32px',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderLeft: '1px solid #1e1e21',
                            cursor: 'pointer'
                          }}
                          title={ring.locked ? "Unlock Layer" : "Lock Layer"}
                          onMouseOver={(evt) => { evt.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)'; }}
                          onMouseOut={(evt) => { evt.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" 
                            stroke={ring.locked ? "#dfdfe3" : "#4b4b52"} 
                            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" 
                            style={{ display: 'block', opacity: ring.locked ? 1 : 0.6 }}
                          >
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                        </div>

                        {/* Column 5: Visibility Eye */}
                        <div
                          onClick={(evt) => {
                            evt.stopPropagation();
                            setRings(rings.map(r => r.id === ring.id ? { ...r, visible: r.visible === false ? true : false } : r));
                          }}
                          style={{
                            width: '32px',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderLeft: '1px solid #1e1e21',
                            cursor: 'pointer'
                          }}
                          title={ring.visible === false ? "Show Layer" : "Hide Layer"}
                          onMouseOver={(evt) => { evt.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)'; }}
                          onMouseOut={(evt) => { evt.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          {ring.visible === false ? (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#4b4b52" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.6 }}>
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                              <line x1="1" y1="1" x2="23" y2="23" />
                            </svg>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#dfdfe3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Bottom Toolbar */}
                <div style={{
                  height: '32px',
                  backgroundColor: '#1e1e21',
                  borderTop: '1px solid #232326',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 8px',
                  gap: '12px'
                }}>
                  {/* Create Group Folder Button */}
                  <button
                    type="button"
                    onClick={addGroup}
                    title="Create Folder Group"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#8e8e93',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '2px'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#ffffff'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#8e8e93'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'block' }}>
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    </svg>
                  </button>

                  {/* Duplicate Layer Button */}
                  <button
                    type="button"
                    onClick={duplicateSelectedRing}
                    disabled={!selectedRingId}
                    title="Duplicate Layer"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: selectedRingId ? '#8e8e93' : '#38383d',
                      cursor: selectedRingId ? 'pointer' : 'default',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '2px'
                    }}
                    onMouseOver={(e) => { if (selectedRingId) e.currentTarget.style.color = '#ffffff'; }}
                    onMouseOut={(e) => { if (selectedRingId) e.currentTarget.style.color = '#8e8e93'; }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'block' }}>
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                  </button>

                  {/* Vertical Separator */}
                  <div style={{ width: '1px', height: '14px', backgroundColor: '#2d2d30' }} />

                  {/* Copy to Other Side (Disabled Placeholder for visual layout parity) */}
                  <button
                    type="button"
                    disabled={true}
                    title="Duplicate to Other Side (Not applicable to coin face)"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#38383d',
                      cursor: 'default',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '2px'
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'block' }}>
                      <rect x="2" y="9" width="11" height="11" rx="2" ry="2" />
                      <path d="M9 5H4a2 2 0 0 0-2 2v2" />
                      <path d="M17 2l4 4-4 4" />
                      <path d="M13 14h3a5 5 0 0 0 5-5V6" />
                    </svg>
                  </button>

                  {/* Delete Selected Layer Button */}
                  <button
                    type="button"
                    onClick={() => {
                      if (selectedRingId) {
                        deleteRing(selectedRingId);
                        setSelectedRingId(null);
                      }
                    }}
                    disabled={!selectedRingId}
                    title="Delete Layer"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: selectedRingId ? '#8e8e93' : '#38383d',
                      cursor: selectedRingId ? 'pointer' : 'default',
                      marginLeft: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '2px'
                    }}
                    onMouseOver={(e) => { if (selectedRingId) e.currentTarget.style.color = '#f85149'; }}
                    onMouseOut={(e) => { if (selectedRingId) e.currentTarget.style.color = '#8e8e93'; }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'block' }}>
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* PROPERTIES TAB */}
            {activeRightTab === 'properties' && (
              <div style={{ padding: '20px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                
                {/* 1. Ring Layer Properties */}
                {activeRing && activeRing.type !== 'image' && activeRing.type !== 'text' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', opacity: activeRing.locked ? 0.6 : 1 }}>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label style={{ fontSize: '11px', fontWeight: '600', color: '#8e8e93' }}>Grayscale Value</label>
                        <span style={{ fontSize: '11px', color: '#8e8e93' }}>{activeRing.grayscale}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        disabled={activeRing.locked}
                        value={activeRing.grayscale}
                        onMouseDown={saveHistoryState}
                        onChange={(e) => updateSelectedRing('grayscale', parseInt(e.target.value))}
                        style={{ width: '100%', height: '6px', cursor: 'pointer' }}
                      />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label style={{ fontSize: '11px', fontWeight: '600', color: '#8e8e93' }}>Outer Radius (RX & RY)</label>
                        <button
                          type="button"
                          disabled={activeRing.locked}
                          onClick={() => updateSelectedRingWithHistory('constrainOuter', !activeRing.constrainOuter)}
                          style={{ background: 'none', border: 'none', color: activeRing.constrainOuter ? '#58a6ff' : '#8e8e93', cursor: 'pointer', fontSize: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <span>{activeRing.constrainOuter ? "Locked (Circle)" : "Unlocked"}</span>
                        </button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                            <span>RX</span><span>{activeRing.outerRadiusX}px</span>
                          </div>
                          <input
                            type="range"
                            min="45"
                            max="300"
                            disabled={activeRing.locked}
                            value={activeRing.outerRadiusX}
                            onMouseDown={saveHistoryState}
                            onChange={(e) => updateSelectedRing('outerRadiusX', parseInt(e.target.value))}
                            style={{ width: '100%' }}
                          />
                        </div>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                            <span>RY</span><span>{activeRing.outerRadiusY}px</span>
                          </div>
                          <input
                            type="range"
                            min="45"
                            max="300"
                            disabled={activeRing.locked || activeRing.constrainOuter}
                            value={activeRing.outerRadiusY}
                            onMouseDown={saveHistoryState}
                            onChange={(e) => updateSelectedRing('outerRadiusY', parseInt(e.target.value))}
                            style={{ width: '100%', opacity: activeRing.constrainOuter ? 0.4 : 1 }}
                          />
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label style={{ fontSize: '11px', fontWeight: '600', color: '#8e8e93' }}>Inner Radius (RX & RY)</label>
                        <button
                          type="button"
                          disabled={activeRing.locked}
                          onClick={() => updateSelectedRingWithHistory('constrainInner', !activeRing.constrainInner)}
                          style={{ background: 'none', border: 'none', color: activeRing.constrainInner ? '#58a6ff' : '#8e8e93', cursor: 'pointer', fontSize: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <span>{activeRing.constrainInner ? "Locked (Circle)" : "Unlocked"}</span>
                        </button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                            <span>RX</span><span>{activeRing.innerRadiusX}px</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="300"
                            disabled={activeRing.locked}
                            value={activeRing.innerRadiusX}
                            onMouseDown={saveHistoryState}
                            onChange={(e) => updateSelectedRing('innerRadiusX', parseInt(e.target.value))}
                            style={{ width: '100%' }}
                          />
                        </div>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                            <span>RY</span><span>{activeRing.innerRadiusY}px</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="300"
                            disabled={activeRing.locked || activeRing.constrainInner}
                            value={activeRing.innerRadiusY}
                            onMouseDown={saveHistoryState}
                            onChange={(e) => updateSelectedRing('innerRadiusY', parseInt(e.target.value))}
                            style={{ width: '100%', opacity: activeRing.constrainInner ? 0.4 : 1 }}
                          />
                        </div>
                      </div>
                    </div>



                    {/* Collapsible BLUR Panel for Ring */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                      <div 
                        onClick={() => setBlurExpanded(prev => !prev)}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px', 
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                          {blurExpanded ? '▼' : '▶'}
                        </span>
                        <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Blur
                        </span>
                      </div>

                      {blurExpanded && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '11px', color: '#dfdfe3', userSelect: 'none' }}>
                            <input
                              type="checkbox"
                              disabled={activeRing.locked}
                              checked={activeRing.enableBlur === true}
                              onChange={(e) => updateSelectedRing('enableBlur', e.target.checked)}
                              style={{ width: '13px', height: '13px', accentColor: '#007aff', cursor: activeRing.locked ? 'default' : 'pointer' }}
                            />
                            <span>Enable Blur Effect</span>
                          </label>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', opacity: (activeRing.enableBlur === true) ? 1 : 0.4 }}>
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                <span>Outer Blur</span><span>{activeRing.outerBlur || 0}px</span>
                              </div>
                              <input
                                type="range"
                                min="0"
                                max="10"
                                step="0.5"
                                disabled={activeRing.locked || activeRing.enableBlur !== true}
                                value={activeRing.outerBlur || 0}
                                onChange={(e) => updateSelectedRing('outerBlur', parseFloat(e.target.value))}
                                style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: (activeRing.locked || activeRing.enableBlur !== true) ? 'default' : 'pointer' }}
                              />
                            </div>
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                <span>Inner Blur</span><span>{activeRing.innerBlur || 0}px</span>
                              </div>
                              <input
                                type="range"
                                min="0"
                                max="10"
                                step="0.5"
                                disabled={activeRing.locked || activeRing.enableBlur !== true}
                                value={activeRing.innerBlur || 0}
                                onChange={(e) => updateSelectedRing('innerBlur', parseFloat(e.target.value))}
                                style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: (activeRing.locked || activeRing.enableBlur !== true) ? 'default' : 'pointer' }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Collapsible Drop Shadow Panel */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                      <div 
                        onClick={() => setShadowExpanded(prev => !prev)}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px', 
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                          {shadowExpanded ? '▼' : '▶'}
                        </span>
                        <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Drop Shadow
                        </span>
                      </div>

                      {shadowExpanded && activeRing && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '11px', color: '#dfdfe3', userSelect: 'none' }}>
                            <input
                              type="checkbox"
                              disabled={activeRing.locked}
                              checked={!!activeRing.shadowEnabled}
                              onChange={(e) => updateSelectedRing('shadowEnabled', e.target.checked)}
                              style={{ width: '13px', height: '13px', accentColor: '#007aff', cursor: activeRing.locked ? 'default' : 'pointer' }}
                            />
                            <span>Enable Drop Shadow</span>
                          </label>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', opacity: activeRing.shadowEnabled ? 1 : 0.4 }}>
                            {/* Offsets in a grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                  <span>Offset X</span><span>{activeRing.shadowOffsetX !== undefined ? activeRing.shadowOffsetX : 2}px</span>
                                </div>
                                <input
                                  type="range"
                                  min="-20"
                                  max="20"
                                  step="1"
                                  disabled={activeRing.locked || !activeRing.shadowEnabled}
                                  value={activeRing.shadowOffsetX !== undefined ? activeRing.shadowOffsetX : 2}
                                  onChange={(e) => updateSelectedRing('shadowOffsetX', parseInt(e.target.value))}
                                  style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: (activeRing.locked || !activeRing.shadowEnabled) ? 'default' : 'pointer' }}
                                />
                              </div>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                  <span>Offset Y</span><span>{activeRing.shadowOffsetY !== undefined ? activeRing.shadowOffsetY : 2}px</span>
                                </div>
                                <input
                                  type="range"
                                  min="-20"
                                  max="20"
                                  step="1"
                                  disabled={activeRing.locked || !activeRing.shadowEnabled}
                                  value={activeRing.shadowOffsetY !== undefined ? activeRing.shadowOffsetY : 2}
                                  onChange={(e) => updateSelectedRing('shadowOffsetY', parseInt(e.target.value))}
                                  style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: (activeRing.locked || !activeRing.shadowEnabled) ? 'default' : 'pointer' }}
                                />
                              </div>
                            </div>

                            {/* Blur & Opacity in a grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                  <span>Blur Radius</span><span>{activeRing.shadowBlur !== undefined ? activeRing.shadowBlur : 3}px</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="20"
                                  step="0.5"
                                  disabled={activeRing.locked || !activeRing.shadowEnabled}
                                  value={activeRing.shadowBlur !== undefined ? activeRing.shadowBlur : 3}
                                  onChange={(e) => updateSelectedRing('shadowBlur', parseFloat(e.target.value))}
                                  style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: (activeRing.locked || !activeRing.shadowEnabled) ? 'default' : 'pointer' }}
                                />
                              </div>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                  <span>Opacity</span><span>{Math.round((activeRing.shadowOpacity !== undefined ? activeRing.shadowOpacity : 0.5) * 100)}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.05"
                                  disabled={activeRing.locked || !activeRing.shadowEnabled}
                                  value={activeRing.shadowOpacity !== undefined ? activeRing.shadowOpacity : 0.5}
                                  onChange={(e) => updateSelectedRing('shadowOpacity', parseFloat(e.target.value))}
                                  style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: (activeRing.locked || !activeRing.shadowEnabled) ? 'default' : 'pointer' }}
                                />
                              </div>
                            </div>

                            {/* Color picker */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '10px', color: '#8e8e93' }}>Shadow Color</span>
                              <input
                                type="color"
                                disabled={activeRing.locked || !activeRing.shadowEnabled}
                                value={activeRing.shadowColor || '#000000'}
                                onChange={(e) => updateSelectedRing('shadowColor', e.target.value)}
                                style={{
                                  border: 'none',
                                  width: '32px',
                                  height: '18px',
                                  padding: 0,
                                  backgroundColor: 'transparent',
                                  cursor: (activeRing.locked || !activeRing.shadowEnabled) ? 'default' : 'pointer'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                        <span>Noise Grain</span><span>{activeRing.noise}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        disabled={activeRing.locked}
                        value={activeRing.noise}
                        onChange={(e) => updateSelectedRing('noise', parseInt(e.target.value))}
                        style={{ width: '100%' }}
                      />
                    </div>

                    {activeRing.noise > 0 && (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                          <span>Noise size</span><span>{activeRing.noiseSize || 50}%</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="100"
                          disabled={activeRing.locked}
                          value={activeRing.noiseSize || 50}
                          onChange={(e) => updateSelectedRing('noiseSize', parseInt(e.target.value))}
                          style={{ width: '100%' }}
                        />
                      </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#8e8e93' }}>Texture Pattern Overlay</label>
                      <select
                        disabled={activeRing.locked}
                        value={activeRing.patternUrl || ''}
                        onChange={(e) => updateSelectedRing('patternUrl', e.target.value)}
                        style={{ backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '6px', padding: '6px 10px', color: '#ffffff', fontSize: '12px', outline: 'none', cursor: 'pointer' }}
                      >
                        <option value="">(None - Solid Grayscale)</option>
                        {(localStudioAssets || studioAssets || []).map(asset => (
                          <option key={asset.id} value={asset.file_url || asset.location}>
                            {asset.original_name || asset.original_filename || asset.filename}
                          </option>
                        ))}
                      </select>
                    </div>


                  </div>
                )}

                {/* 2. Image Layer Properties */}
                {activeRing && activeRing.type === 'image' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', opacity: activeRing.locked ? 0.6 : 1 }}>

                    {/* Source Image & Change Image */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#8e8e93' }}>Source Image</label>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        backgroundColor: '#1b1b1d',
                        border: '1px solid #2d2d30',
                        borderRadius: '6px',
                        padding: '8px'
                      }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          backgroundColor: '#111111',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          {activeRing.imageUrl ? (
                            <img
                              src={activeRing.imageUrl}
                              alt="Source"
                              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#8e8e93' }}>
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <polyline points="21 15 16 10 5 21" />
                            </svg>
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{
                            fontSize: '11px',
                            color: '#dfdfe3',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }} title={activeRing.name || 'Image'}>
                            {activeRing.name || 'Image'}
                          </span>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '2px', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              data-testid="change-image-source-btn"
                              disabled={activeRing.locked}
                              onClick={() => {
                                setIsChangingImageSource(true);
                                setChangingImageRingId(activeRing.id);
                                setShowAssetModal(true);
                              }}
                              style={{
                                backgroundColor: '#262629',
                                border: '1px solid #3c3c42',
                                borderRadius: '4px',
                                color: '#58a6ff',
                                fontSize: '10px',
                                fontWeight: '600',
                                padding: '2px 8px',
                                cursor: activeRing.locked ? 'not-allowed' : 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
                              </svg>
                              <span>Change Image</span>
                            </button>

                            <input
                              ref={directUploadInputRef}
                              type="file"
                              accept="image/*,.svg"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                if (e.target.files && e.target.files.length > 0) {
                                  handleUploadAssetFiles(e.target.files, { replaceRingId: activeRing.id });
                                  e.target.value = '';
                                }
                              }}
                            />
                            <button
                              type="button"
                              data-testid="upload-direct-image-btn"
                              disabled={activeRing.locked || isUploadingAsset}
                              onClick={() => directUploadInputRef.current && directUploadInputRef.current.click()}
                              style={{
                                backgroundColor: isUploadingAsset ? '#3c3c42' : '#1677ff',
                                border: 'none',
                                borderRadius: '4px',
                                color: '#ffffff',
                                fontSize: '10px',
                                fontWeight: '600',
                                padding: '2px 8px',
                                cursor: (activeRing.locked || isUploadingAsset) ? 'not-allowed' : 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                              </svg>
                              <span>{isUploadingAsset ? 'Uploading...' : 'Upload New'}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Pan & Zoom Crop Section */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label style={{ fontSize: '11px', fontWeight: '600', color: '#8e8e93' }}>Pan &amp; Zoom Crop</label>
                        {((activeRing.imgZoom && activeRing.imgZoom > 1.001) || activeRing.imgPanX || activeRing.imgPanY) && (
                          <button
                            type="button"
                            data-testid="reset-crop-btn"
                            disabled={activeRing.locked}
                            onClick={() => handleResetImageCrop(activeRing.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#ff7b72',
                              fontSize: '10px',
                              cursor: activeRing.locked ? 'not-allowed' : 'pointer',
                              padding: 0
                            }}
                          >
                            Reset Crop
                          </button>
                        )}
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        backgroundColor: '#1b1b1d',
                        border: '1px solid #2d2d30',
                        borderRadius: '6px',
                        padding: '8px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                          <span>Zoom: {Math.round((activeRing.imgZoom || 1) * 100)}%</span>
                          <span>Pan: ({Math.round(activeRing.imgPanX || 0)}%, {Math.round(activeRing.imgPanY || 0)}%)</span>
                        </div>
                        <button
                          type="button"
                          data-testid="adjust-pan-zoom-btn"
                          disabled={activeRing.locked || !activeRing.imageUrl}
                          onClick={() => openImageAdjustModal(activeRing)}
                          style={{
                            width: '100%',
                            backgroundColor: '#1677ff',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 10px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: activeRing.locked || !activeRing.imageUrl ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                          <span>Adjust Pan &amp; Zoom...</span>
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label style={{ fontSize: '11px', fontWeight: '600', color: '#8e8e93' }}>Image Size</label>
                        <button
                          type="button"
                          disabled={activeRing.locked}
                          onClick={() => updateSelectedRing('constrainSize', activeRing.constrainSize !== false ? false : true)}
                          style={{ background: 'none', border: 'none', color: activeRing.constrainSize !== false ? '#58a6ff' : '#8e8e93', cursor: 'pointer', fontSize: '10px' }}
                        >
                          <span>{activeRing.constrainSize !== false ? "Proportional" : "Independent"}</span>
                        </button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                            <span>Width</span><span>{activeRing.width}px</span>
                          </div>
                          <input
                            type="range"
                            min="20"
                            max="200"
                            disabled={activeRing.locked}
                            value={activeRing.width}
                            onChange={(e) => updateSelectedRing('width', parseInt(e.target.value))}
                            style={{ width: '100%' }}
                          />
                        </div>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                            <span>Height</span><span>{activeRing.height}px</span>
                          </div>
                          <input
                            type="range"
                            min="20"
                            max="200"
                            disabled={activeRing.locked || activeRing.constrainSize !== false}
                            value={activeRing.height}
                            onChange={(e) => updateSelectedRing('height', parseInt(e.target.value))}
                            style={{ width: '100%', opacity: activeRing.constrainSize !== false ? 0.4 : 1 }}
                          />
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                        <span style={{ fontSize: '11px', fontWeight: '600', color: '#8e8e93' }}>Rotation Angle</span>
                        <span>{activeRing.angle || 0}°</span>
                      </div>
                      <input
                        type="range"
                        min="-180"
                        max="180"
                        step="1"
                        disabled={activeRing.locked}
                        value={activeRing.angle || 0}
                        onMouseDown={saveHistoryState}
                        onChange={(e) => updateSelectedRing('angle', parseInt(e.target.value))}
                        style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: activeRing.locked ? 'default' : 'pointer' }}
                      />
                    </div>

                    {/* Tone, Invert & Orientation Controls */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #2d2d30', paddingTop: '10px', marginTop: '2px' }}>
                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#8e8e93' }}>Tone, Invert &amp; Orientation</label>

                      {/* Grayscale and Invert Buttons in 2-Column Grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                        {/* Grayscale Button */}
                        <button
                          type="button"
                          data-testid="grayscale-image-btn"
                          disabled={activeRing.locked}
                          onClick={() => {
                            const isCurrentlyGray = !!(activeRing.isGrayscale || activeRing.imageGrayscale || activeRing.grayscale === true);
                            updateSelectedRingWithHistory('isGrayscale', !isCurrentlyGray);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '6px 8px',
                            backgroundColor: (activeRing.isGrayscale || activeRing.imageGrayscale || activeRing.grayscale === true) ? 'rgba(22, 119, 255, 0.2)' : '#1b1b1d',
                            border: `1px solid ${(activeRing.isGrayscale || activeRing.imageGrayscale || activeRing.grayscale === true) ? '#1677ff' : '#3c3c42'}`,
                            borderRadius: '5px',
                            color: (activeRing.isGrayscale || activeRing.imageGrayscale || activeRing.grayscale === true) ? '#58a6ff' : '#dfdfe3',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: activeRing.locked ? 'not-allowed' : 'pointer'
                          }}
                          title="Convert color image to black and white grayscale"
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="3" width="18" height="18" rx="2" />
                              <line x1="3" y1="12" x2="21" y2="12" />
                              <line x1="12" y1="3" x2="12" y2="21" />
                            </svg>
                            <span>Grayscale</span>
                          </span>
                          <span style={{
                            fontSize: '9px',
                            padding: '1px 5px',
                            borderRadius: '3px',
                            backgroundColor: (activeRing.isGrayscale || activeRing.imageGrayscale || activeRing.grayscale === true) ? '#1677ff' : '#2b2b2f',
                            color: '#ffffff'
                          }}>
                            {(activeRing.isGrayscale || activeRing.imageGrayscale || activeRing.grayscale === true) ? 'ON' : 'OFF'}
                          </span>
                        </button>

                        {/* Invert Colors Button */}
                        <button
                          type="button"
                          data-testid="invert-image-btn"
                          disabled={activeRing.locked}
                          onClick={() => updateSelectedRingWithHistory('invertColors', !activeRing.invertColors)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '6px 8px',
                            backgroundColor: activeRing.invertColors ? 'rgba(22, 119, 255, 0.2)' : '#1b1b1d',
                            border: `1px solid ${activeRing.invertColors ? '#1677ff' : '#3c3c42'}`,
                            borderRadius: '5px',
                            color: activeRing.invertColors ? '#58a6ff' : '#dfdfe3',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: activeRing.locked ? 'not-allowed' : 'pointer'
                          }}
                          title="Invert image tones (negative stamp)"
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M12 2a10 10 0 0 1 0 20z" fill="currentColor" />
                            </svg>
                            <span>Invert Colors</span>
                          </span>
                          <span style={{
                            fontSize: '9px',
                            padding: '1px 5px',
                            borderRadius: '3px',
                            backgroundColor: activeRing.invertColors ? '#1677ff' : '#2b2b2f',
                            color: '#ffffff'
                          }}>
                            {activeRing.invertColors ? 'ON' : 'OFF'}
                          </span>
                        </button>
                      </div>

                      {/* Monochrome Button */}
                      <button
                        type="button"
                        data-testid="monochrome-image-btn"
                        disabled={activeRing.locked}
                        onClick={() => {
                          const nextMono = !activeRing.monochrome;
                          saveHistoryState();
                          if (nextMono && !(activeRing.isGrayscale || activeRing.imageGrayscale || activeRing.grayscale === true)) {
                            updateSelectedRing('isGrayscale', true);
                          }
                          updateSelectedRing('monochrome', nextMono);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '6px 10px',
                          backgroundColor: activeRing.monochrome ? 'rgba(22, 119, 255, 0.2)' : '#1b1b1d',
                          border: `1px solid ${activeRing.monochrome ? '#1677ff' : '#3c3c42'}`,
                          borderRadius: '5px',
                          color: activeRing.monochrome ? '#58a6ff' : '#dfdfe3',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: activeRing.locked ? 'not-allowed' : 'pointer'
                        }}
                        title="Clip grayscale into high-contrast 2-tone monochrome engraving stamp"
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <polygon points="3,3 21,3 3,21" fill="currentColor" stroke="none" />
                          </svg>
                          <span>Monochrome (Threshold Clip)</span>
                        </span>
                        <span style={{
                          fontSize: '9px',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          backgroundColor: activeRing.monochrome ? '#1677ff' : '#2b2b2f',
                          color: '#ffffff'
                        }}>
                          {activeRing.monochrome ? 'ON' : 'OFF'}
                        </span>
                      </button>

                      {/* Monochrome Contrast & Threshold Sliders (Shown when Monochrome is ON) */}
                      {activeRing.monochrome && (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          padding: '8px 10px',
                          backgroundColor: '#161618',
                          border: '1px solid #2d2d30',
                          borderRadius: '6px'
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                              <span>Contrast / Clipping Level</span>
                              <span style={{ fontFamily: 'monospace', color: '#58a6ff' }}>
                                {activeRing.monochromeContrast !== undefined ? activeRing.monochromeContrast : (activeRing.contrast !== undefined ? activeRing.contrast : 400)}%
                              </span>
                            </div>
                            <input
                              type="range"
                              data-testid="monochrome-contrast-slider"
                              min="100"
                              max="1000"
                              step="10"
                              disabled={activeRing.locked}
                              value={activeRing.monochromeContrast !== undefined ? activeRing.monochromeContrast : (activeRing.contrast !== undefined ? activeRing.contrast : 400)}
                              onMouseDown={saveHistoryState}
                              onChange={(e) => updateSelectedRing('monochromeContrast', parseInt(e.target.value))}
                              style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: activeRing.locked ? 'default' : 'pointer' }}
                            />
                            <div style={{ fontSize: '9px', color: '#71717a' }}>
                              Higher contrast steepens midtones into solid black or white
                            </div>
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                              <span>Clipping Threshold (Cutoff)</span>
                              <span style={{ fontFamily: 'monospace', color: '#58a6ff' }}>
                                {activeRing.monochromeThreshold !== undefined ? activeRing.monochromeThreshold : 50}%
                              </span>
                            </div>
                            <input
                              type="range"
                              data-testid="monochrome-threshold-slider"
                              min="5"
                              max="95"
                              step="1"
                              disabled={activeRing.locked}
                              value={activeRing.monochromeThreshold !== undefined ? activeRing.monochromeThreshold : 50}
                              onMouseDown={saveHistoryState}
                              onChange={(e) => updateSelectedRing('monochromeThreshold', parseInt(e.target.value))}
                              style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: activeRing.locked ? 'default' : 'pointer' }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#71717a' }}>
                              <span>More solid black</span>
                              <span>More solid white</span>
                            </div>
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                        <button
                          type="button"
                          data-testid="flip-h-image-btn"
                          disabled={activeRing.locked}
                          onClick={() => updateSelectedRingWithHistory('flipH', !activeRing.flipH)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            padding: '5px 8px',
                            backgroundColor: activeRing.flipH ? 'rgba(22, 119, 255, 0.2)' : '#1b1b1d',
                            border: `1px solid ${activeRing.flipH ? '#1677ff' : '#3c3c42'}`,
                            borderRadius: '4px',
                            color: activeRing.flipH ? '#58a6ff' : '#dfdfe3',
                            fontSize: '10px',
                            fontWeight: '600',
                            cursor: activeRing.locked ? 'not-allowed' : 'pointer'
                          }}
                          title="Flip horizontally (mirror left-to-right)"
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="8 7 3 12 8 17" />
                              <polyline points="16 7 21 12 16 17" />
                              <line x1="3" y1="12" x2="21" y2="12" />
                            </svg>
                            <span>Flip H</span>
                          </span>
                        </button>
                        <button
                          type="button"
                          data-testid="flip-v-image-btn"
                          disabled={activeRing.locked}
                          onClick={() => updateSelectedRingWithHistory('flipV', !activeRing.flipV)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            padding: '5px 8px',
                            backgroundColor: activeRing.flipV ? 'rgba(22, 119, 255, 0.2)' : '#1b1b1d',
                            border: `1px solid ${activeRing.flipV ? '#1677ff' : '#3c3c42'}`,
                            borderRadius: '4px',
                            color: activeRing.flipV ? '#58a6ff' : '#dfdfe3',
                            fontSize: '10px',
                            fontWeight: '600',
                            cursor: activeRing.locked ? 'not-allowed' : 'pointer'
                          }}
                          title="Flip vertically (mirror upside-down)"
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="7 8 12 3 17 8" />
                              <polyline points="7 16 12 21 17 16" />
                              <line x1="12" y1="3" x2="12" y2="21" />
                            </svg>
                            <span>Flip V</span>
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* Collapsible BLUR Panel for Image */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                      <div 
                        onClick={() => setBlurExpanded(prev => !prev)}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px', 
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                          {blurExpanded ? '▼' : '▶'}
                        </span>
                        <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Blur
                        </span>
                      </div>

                      {blurExpanded && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '11px', color: '#dfdfe3', userSelect: 'none' }}>
                            <input
                              type="checkbox"
                              disabled={activeRing.locked}
                              checked={activeRing.enableBlur === true}
                              onChange={(e) => updateSelectedRing('enableBlur', e.target.checked)}
                              style={{ width: '13px', height: '13px', accentColor: '#007aff', cursor: activeRing.locked ? 'default' : 'pointer' }}
                            />
                            <span>Enable Blur Effect</span>
                          </label>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', opacity: (activeRing.enableBlur === true) ? 1 : 0.4 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                              <span>Gaussian Blur</span><span>{activeRing.gaussianBlur || 0}px</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="20"
                              step="0.5"
                              disabled={activeRing.locked || activeRing.enableBlur !== true}
                              value={activeRing.gaussianBlur || 0}
                              onChange={(e) => updateSelectedRing('gaussianBlur', parseFloat(e.target.value))}
                              style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: (activeRing.locked || activeRing.enableBlur !== true) ? 'default' : 'pointer' }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Collapsible Drop Shadow Panel */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                      <div 
                        onClick={() => setShadowExpanded(prev => !prev)}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px', 
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                          {shadowExpanded ? '▼' : '▶'}
                        </span>
                        <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Drop Shadow
                        </span>
                      </div>

                      {shadowExpanded && activeRing && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '11px', color: '#dfdfe3', userSelect: 'none' }}>
                            <input
                              type="checkbox"
                              disabled={activeRing.locked}
                              checked={!!activeRing.shadowEnabled}
                              onChange={(e) => updateSelectedRing('shadowEnabled', e.target.checked)}
                              style={{ width: '13px', height: '13px', accentColor: '#007aff', cursor: activeRing.locked ? 'default' : 'pointer' }}
                            />
                            <span>Enable Drop Shadow</span>
                          </label>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', opacity: activeRing.shadowEnabled ? 1 : 0.4 }}>
                            {/* Offsets in a grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                  <span>Offset X</span><span>{activeRing.shadowOffsetX !== undefined ? activeRing.shadowOffsetX : 2}px</span>
                                </div>
                                <input
                                  type="range"
                                  min="-20"
                                  max="20"
                                  step="1"
                                  disabled={activeRing.locked || !activeRing.shadowEnabled}
                                  value={activeRing.shadowOffsetX !== undefined ? activeRing.shadowOffsetX : 2}
                                  onChange={(e) => updateSelectedRing('shadowOffsetX', parseInt(e.target.value))}
                                  style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: (activeRing.locked || !activeRing.shadowEnabled) ? 'default' : 'pointer' }}
                                />
                              </div>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                  <span>Offset Y</span><span>{activeRing.shadowOffsetY !== undefined ? activeRing.shadowOffsetY : 2}px</span>
                                </div>
                                <input
                                  type="range"
                                  min="-20"
                                  max="20"
                                  step="1"
                                  disabled={activeRing.locked || !activeRing.shadowEnabled}
                                  value={activeRing.shadowOffsetY !== undefined ? activeRing.shadowOffsetY : 2}
                                  onChange={(e) => updateSelectedRing('shadowOffsetY', parseInt(e.target.value))}
                                  style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: (activeRing.locked || !activeRing.shadowEnabled) ? 'default' : 'pointer' }}
                                />
                              </div>
                            </div>

                            {/* Blur & Opacity in a grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                  <span>Blur Radius</span><span>{activeRing.shadowBlur !== undefined ? activeRing.shadowBlur : 3}px</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="20"
                                  step="0.5"
                                  disabled={activeRing.locked || !activeRing.shadowEnabled}
                                  value={activeRing.shadowBlur !== undefined ? activeRing.shadowBlur : 3}
                                  onChange={(e) => updateSelectedRing('shadowBlur', parseFloat(e.target.value))}
                                  style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: (activeRing.locked || !activeRing.shadowEnabled) ? 'default' : 'pointer' }}
                                />
                              </div>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                  <span>Opacity</span><span>{Math.round((activeRing.shadowOpacity !== undefined ? activeRing.shadowOpacity : 0.5) * 100)}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.05"
                                  disabled={activeRing.locked || !activeRing.shadowEnabled}
                                  value={activeRing.shadowOpacity !== undefined ? activeRing.shadowOpacity : 0.5}
                                  onChange={(e) => updateSelectedRing('shadowOpacity', parseFloat(e.target.value))}
                                  style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: (activeRing.locked || !activeRing.shadowEnabled) ? 'default' : 'pointer' }}
                                />
                              </div>
                            </div>

                            {/* Color picker */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '10px', color: '#8e8e93' }}>Shadow Color</span>
                              <input
                                type="color"
                                disabled={activeRing.locked || !activeRing.shadowEnabled}
                                value={activeRing.shadowColor || '#000000'}
                                onChange={(e) => updateSelectedRing('shadowColor', e.target.value)}
                                style={{
                                  border: 'none',
                                  width: '32px',
                                  height: '18px',
                                  padding: 0,
                                  backgroundColor: 'transparent',
                                  cursor: (activeRing.locked || !activeRing.shadowEnabled) ? 'default' : 'pointer'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>


                  </div>
                )}

                {/* 3. Typography Global Properties */}
                {(activeTool === 'text' || (activeRing && activeRing.type === 'text')) && (() => {
                  const isTypographyLayer = !activeRing || activeRing.id === 'typography-layer' || activeRing.isCurved;
                  const isLocked = activeRing ? activeRing.locked : isTextLayerLocked();

                  const isPrimary = !activeRing || activeRing.id === 'typography-layer';
                  
                  const activeMode = isPrimary ? textMode : (activeRing.textMode || 'single');
                  const activeUpper = isPrimary ? upperText : (activeRing.upperText || '');
                  const activeLower = isPrimary ? lowerText : (activeRing.lowerText || '');
                  const activeSingle = isPrimary ? singleText : (activeRing.singleText || '');
                  const activeGrayscale = isPrimary ? textGrayscale : (activeRing.grayscale !== undefined ? activeRing.grayscale : 100);
                  const activeRadius = isPrimary ? textRadius : (activeRing.textRadius || 68);
                  const activeFont = isPrimary ? textFont : (activeRing.fontFamily || '"Arial Black", sans-serif');
                  const activeWeight = isPrimary ? (textFontWeight || '900') : (activeRing.fontWeight || 'normal');
                  const activeSize = isPrimary ? textSize : (activeRing.fontSize || 16);
                  const activeFontStyle = isPrimary ? textFontStyle : (activeRing.fontStyle || 'normal');
                  const activeKerning = isPrimary ? textKerning : (activeRing.letterSpacing !== undefined ? activeRing.letterSpacing : 1.2);

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', opacity: isLocked ? 0.6 : 1 }}>
                      {isTypographyLayer ? (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontSize: '11px', fontWeight: '600', color: '#8e8e93' }}>Split Text</span>
                            <button
                              type="button"
                              disabled={isLocked}
                              onClick={() => {
                                const nextMode = activeMode === 'split' ? 'single' : 'split';
                                isPrimary ? setTextMode(nextMode) : updateSelectedRing('textMode', nextMode);
                              }}
                              style={{
                                backgroundColor: activeMode === 'split' ? '#007aff' : '#3a3a3c',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '3px 12px',
                                fontSize: '11px',
                                fontWeight: '600',
                                cursor: isLocked ? 'default' : 'pointer',
                                transition: 'background-color 0.2s'
                              }}
                            >
                              {activeMode === 'split' ? 'ON' : 'OFF'}
                            </button>
                          </div>

                          {activeMode === 'split' ? (
                            <>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <label style={{ fontSize: '11px', fontWeight: '600', color: '#8e8e93' }}>Upper Curved Text</label>
                                <input
                                  type="text"
                                  maxLength={32}
                                  disabled={isLocked}
                                  value={activeUpper}
                                  onChange={(e) => {
                                    const val = e.target.value.toUpperCase();
                                    isPrimary ? setUpperText(val) : updateSelectedRing('upperText', val);
                                  }}
                                  style={{ backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '6px', padding: '6px 10px', color: '#ffffff', fontSize: '12px', outline: 'none' }}
                                />
                              </div>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <label style={{ fontSize: '11px', fontWeight: '600', color: '#8e8e93' }}>Lower Curved Text</label>
                                <input
                                  type="text"
                                  maxLength={32}
                                  disabled={isLocked}
                                  value={activeLower}
                                  onChange={(e) => {
                                    const val = e.target.value.toUpperCase();
                                    isPrimary ? setLowerText(val) : updateSelectedRing('lowerText', val);
                                  }}
                                  style={{ backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '6px', padding: '6px 10px', color: '#ffffff', fontSize: '12px', outline: 'none' }}
                                />
                              </div>
                            </>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <label style={{ fontSize: '11px', fontWeight: '600', color: '#8e8e93' }}>Curved Text</label>
                              <input
                                type="text"
                                maxLength={64}
                                disabled={isLocked}
                                value={activeSingle}
                                onChange={(e) => {
                                    const val = e.target.value.toUpperCase();
                                    isPrimary ? setSingleText(val) : updateSelectedRing('singleText', val);
                                }}
                                style={{ backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '6px', padding: '6px 10px', color: '#ffffff', fontSize: '12px', outline: 'none' }}
                              />
                            </div>
                          )}

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                              <span>Text Color Grayscale</span><span>{activeGrayscale}%</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              disabled={isLocked}
                              value={activeGrayscale}
                              onChange={(e) => isPrimary ? setTextGrayscale(parseInt(e.target.value)) : updateSelectedRing('grayscale', parseInt(e.target.value))}
                              style={{ width: '100%' }}
                            />
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                              <span>Text Arc Radius</span><span>{activeRadius}px</span>
                            </div>
                            <input
                              type="range"
                              min="45"
                              max="90"
                              disabled={isLocked}
                              value={activeRadius}
                              onChange={(e) => isPrimary ? setTextRadius(parseInt(e.target.value)) : updateSelectedRing('textRadius', parseInt(e.target.value))}
                              style={{ width: '100%' }}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: '#8e8e93' }}>Text Content</label>
                            <input
                              type="text"
                              maxLength={128}
                              disabled={isLocked}
                              value={activeRing.text || ''}
                              onChange={(e) => updateSelectedRing('text', e.target.value)}
                              style={{ backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '6px', padding: '6px 10px', color: '#ffffff', fontSize: '12px', outline: 'none' }}
                            />
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                              <span>Text Color Grayscale</span><span>{activeRing.grayscale !== undefined ? activeRing.grayscale : 100}%</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              disabled={isLocked}
                              value={activeRing.grayscale !== undefined ? activeRing.grayscale : 100}
                              onChange={(e) => updateSelectedRing('grayscale', parseInt(e.target.value))}
                              style={{ width: '100%' }}
                            />
                          </div>
                        </>
                      )}

                      {/* Collapsible CHARACTER Panel */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                        <div 
                          onClick={() => setCharacterExpanded(prev => !prev)}
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px', 
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                        >
                          <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                            {characterExpanded ? '▼' : '▶'}
                          </span>
                          <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Character
                          </span>
                        </div>

                        {characterExpanded && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                            {/* Modify Text (Text Transform) */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '4px' }}>
                              <span style={{ fontSize: '9px', color: '#8e8e93' }}>Modify Text</span>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                {[
                                  { value: 'none', label: 'None' },
                                  { value: 'uppercase', label: 'ABC' },
                                  { value: 'lowercase', label: 'abc' },
                                  { value: 'capitalize', label: 'Abc' }
                                ].map(opt => {
                                  const activeCaseVal = isPrimary ? textCase : (activeRing.textCase || 'none');
                                  const isSelected = activeCaseVal === opt.value;
                                  return (
                                    <button
                                      key={opt.label}
                                      type="button"
                                      disabled={isLocked}
                                      onClick={() => isPrimary ? setTextCase(opt.value) : updateSelectedRing('textCase', opt.value)}
                                      style={{
                                        flex: 1,
                                        padding: '4px 0',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        borderRadius: '4px',
                                        border: '1px solid #3c3c42',
                                        backgroundColor: isSelected ? '#007aff' : '#1b1b1d',
                                        color: isSelected ? '#ffffff' : '#dfdfe3',
                                        cursor: isLocked ? 'default' : 'pointer',
                                        transition: 'all 0.1s'
                                      }}
                                    >
                                      {opt.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {/* 1. Font Family & Weight */}
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <div style={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontSize: '10px', color: '#8e8e93' }}>Font Family</span>
                                <select
                                  value={activeFont}
                                  disabled={isLocked}
                                  onChange={(e) => isPrimary ? setTextFont(e.target.value) : updateSelectedRing('fontFamily', e.target.value)}
                                  style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none', cursor: isLocked ? 'default' : 'pointer' }}
                                >
                                  {['Impact, Haettenschweiler, sans-serif', '"Arial Black", sans-serif', 'Copperplate, "Copperplate Gothic Bold", serif', '"Trebuchet MS", sans-serif', '"Century Gothic", Futura, sans-serif', '"Franklin Gothic Medium", sans-serif', 'Georgia, serif', '"Palatino Linotype", Palatino, serif', 'Didot, "Bodoni MT", serif', '"Courier New", monospace', 'Optima, sans-serif', 'system-ui, sans-serif'].map(font => {
                                    const name = font.split(',')[0].replace(/"/g, '');
                                    return (
                                      <option key={font} value={font}>{name}</option>
                                    );
                                  })}
                                </select>
                              </div>
                              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontSize: '10px', color: '#8e8e93' }}>Weight</span>
                                <select
                                  value={activeWeight}
                                  disabled={isLocked}
                                  onChange={(e) => isPrimary ? setTextFontWeight(e.target.value) : updateSelectedRing('fontWeight', e.target.value)}
                                  style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none', cursor: isLocked ? 'default' : 'pointer' }}
                                >
                                  <option value="normal">Normal</option>
                                  <option value="bold">Bold</option>
                                  <option value="100">Thin (100)</option>
                                  <option value="300">Light (300)</option>
                                  <option value="400">Regular (400)</option>
                                  <option value="500">Medium (500)</option>
                                  <option value="600">Semi-Bold (600)</option>
                                  <option value="700">Bold (700)</option>
                                  <option value="800">Extra-Bold (800)</option>
                                  <option value="900">Heavy (900)</option>
                                </select>
                              </div>
                            </div>

                            {/* 2. Font Size, Italic Toggle, and Alignment Toggle Row */}
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                  <span>Font Size</span>
                                  <span>{activeSize}px</span>
                                </div>
                                <input
                                  type="range"
                                  min="8"
                                  max="72"
                                  step="0.5"
                                  disabled={isLocked}
                                  value={activeSize}
                                  onChange={(e) => isPrimary ? setTextSize(parseFloat(e.target.value)) : updateSelectedRing('fontSize', parseFloat(e.target.value))}
                                  style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: isLocked ? 'default' : 'pointer' }}
                                />
                              </div>
                              <div style={{ display: 'flex', gap: '4px', height: '24px' }}>
                                {/* Italic toggle button */}
                                <button
                                  type="button"
                                  disabled={isLocked}
                                  onClick={() => isPrimary ? setTextFontStyle(activeFontStyle === 'italic' ? 'normal' : 'italic') : updateSelectedRing('fontStyle', activeRing.fontStyle === 'italic' ? 'normal' : 'italic')}
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    fontStyle: 'italic',
                                    border: '1px solid #3c3c42',
                                    borderRadius: '3px',
                                    backgroundColor: (activeFontStyle === 'italic') ? '#007aff' : '#1b1b1d',
                                    color: '#ffffff',
                                    cursor: isLocked ? 'default' : 'pointer'
                                  }}
                                  title="Italic"
                                >
                                  I
                                </button>
                              </div>
                              {/* Alignment buttons */}
                              <div style={{ display: 'flex', gap: '2px', opacity: isTypographyLayer ? 0.4 : 1, cursor: isTypographyLayer ? 'default' : 'pointer' }}>
                                {['left', 'center', 'right'].map(align => {
                                  const activeAlign = isTypographyLayer ? 'center' : (activeRing.textAlign || 'center');
                                  const isSelected = activeAlign === align;
                                  return (
                                    <button
                                      key={align}
                                      type="button"
                                      disabled={isLocked || isTypographyLayer}
                                      onClick={() => updateSelectedRing('textAlign', align)}
                                      style={{
                                        padding: '4px 6px',
                                        border: '1px solid #3c3c42',
                                        borderRadius: '3px',
                                        backgroundColor: isSelected ? '#007aff' : '#1b1b1d',
                                        color: isSelected ? '#ffffff' : '#8e8e93',
                                        cursor: (isLocked || isTypographyLayer) ? 'default' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '24px',
                                        height: '24px'
                                      }}
                                      title={isTypographyLayer ? `${align} Align (Disabled on Arc)` : `${align} Align`}
                                    >
                                      {align === 'left' ? (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                          <line x1="3" y1="6" x2="21" y2="6" />
                                          <line x1="3" y1="12" x2="15" y2="12" />
                                          <line x1="3" y1="18" x2="18" y2="18" />
                                        </svg>
                                      ) : align === 'center' ? (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                          <line x1="3" y1="6" x2="21" y2="6" />
                                          <line x1="6" y1="12" x2="18" y2="12" />
                                          <line x1="4" y1="18" x2="20" y2="18" />
                                        </svg>
                                      ) : (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                          <line x1="3" y1="6" x2="21" y2="6" />
                                          <line x1="9" y1="12" x2="21" y2="12" />
                                          <line x1="6" y1="18" x2="21" y2="18" />
                                        </svg>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {/* 3. Decoration Fieldset */}
                            <fieldset style={{
                              border: '1px solid #3c3c42',
                              borderRadius: '4px',
                              padding: '8px 10px',
                              margin: '6px 0 0 0',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '8px',
                              opacity: isTypographyLayer ? 0.4 : 1
                            }}>
                              <legend style={{ fontSize: '9px', fontWeight: 'bold', color: '#8e8e93', padding: '0 4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Decoration
                              </legend>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontSize: '9px', color: '#8e8e93' }}>Text Decoration</span>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  {['none', 'underline', 'line-through'].map(dec => {
                                    const activeDec = isTypographyLayer ? 'none' : (activeRing.textDecoration || 'none');
                                    const isSelected = activeDec === dec;
                                    return (
                                      <button
                                        key={dec}
                                        type="button"
                                        disabled={isLocked || isTypographyLayer}
                                        onClick={() => updateSelectedRing('textDecoration', dec)}
                                        style={{
                                          flex: 1,
                                          padding: '4px 0',
                                          fontSize: '10px',
                                          border: '1px solid #3c3c42',
                                          borderRadius: '3px',
                                          backgroundColor: isSelected ? '#007aff' : '#1b1b1d',
                                          color: isSelected ? '#ffffff' : '#8e8e93',
                                          cursor: (isLocked || isTypographyLayer) ? 'default' : 'pointer',
                                          textTransform: 'capitalize'
                                        }}
                                      >
                                        {dec === 'line-through' ? 'Strikethrough' : dec === 'none' ? 'Normal' : dec}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid #232326', paddingTop: '6px', marginTop: '2px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                  <span>Stroke Thickness</span>
                                  <span>{isTypographyLayer ? 0 : (activeRing.strokeWidth || 0)}px</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="10"
                                  value={isTypographyLayer ? 0 : (activeRing.strokeWidth || 0)}
                                  disabled={isLocked || isTypographyLayer}
                                  onChange={(e) => updateSelectedRing('strokeWidth', parseInt(e.target.value))}
                                  style={{ width: '100%', accentColor: isTypographyLayer ? '#3c3c42' : '#007aff', height: '4px', cursor: (isLocked || isTypographyLayer) ? 'default' : 'pointer' }}
                                />
                              </div>
                            </fieldset>

                            {/* 4. Position & Transform Fieldset */}
                            <fieldset style={{
                              border: '1px solid #3c3c42',
                              borderRadius: '4px',
                              padding: '8px 10px',
                              margin: '6px 0 0 0',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '8px'
                            }}>
                              <legend style={{ fontSize: '9px', fontWeight: 'bold', color: '#8e8e93', padding: '0 4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Position & Transform
                              </legend>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {/* Tracking (Letter Spacing) & Leading */}
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                      <span>Tracking</span>
                                      <span>{activeKerning}px</span>
                                    </div>
                                    <input
                                      type="range"
                                      min="-2"
                                      max="15"
                                      step="0.1"
                                      disabled={isLocked}
                                      value={activeKerning}
                                      onChange={(e) => isPrimary ? setTextKerning(parseFloat(e.target.value)) : updateSelectedRing('letterSpacing', parseFloat(e.target.value))}
                                      style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: isLocked ? 'default' : 'pointer' }}
                                    />
                                  </div>
                                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', opacity: isTypographyLayer ? 0.4 : 1 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                      <span>Leading</span>
                                      <span>{isTypographyLayer ? 1.1 : (activeRing.leading || 1.1)}</span>
                                    </div>
                                    <input
                                      type="range"
                                      min="0.5"
                                      max="3.0"
                                      step="0.1"
                                      value={isTypographyLayer ? 1.1 : (activeRing.leading || 1.1)}
                                      disabled={isLocked || isTypographyLayer}
                                      onChange={(e) => updateSelectedRing('leading', parseFloat(e.target.value))}
                                      style={{ width: '100%', accentColor: isTypographyLayer ? '#3c3c42' : '#007aff', height: '4px', cursor: (isLocked || isTypographyLayer) ? 'default' : 'pointer' }}
                                    />
                                  </div>
                                </div>

                                {/* Baseline Shift & Shear (Slant) */}
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', opacity: isTypographyLayer ? 0.4 : 1 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                      <span>Baseline Shift</span>
                                      <span>{isTypographyLayer ? 0 : (activeRing.baselineShift || 0)}px</span>
                                    </div>
                                    <input
                                      type="range"
                                      min="-20"
                                      max="20"
                                      value={isTypographyLayer ? 0 : (activeRing.baselineShift || 0)}
                                      disabled={isLocked || isTypographyLayer}
                                      onChange={(e) => updateSelectedRing('baselineShift', parseInt(e.target.value))}
                                      style={{ width: '100%', accentColor: isTypographyLayer ? '#3c3c42' : '#007aff', height: '4px', cursor: (isLocked || isTypographyLayer) ? 'default' : 'pointer' }}
                                    />
                                  </div>
                                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', opacity: isTypographyLayer ? 0.4 : 1 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                      <span>Shear (Slant)</span>
                                      <span>{isTypographyLayer ? 0 : (activeRing.shear || 0)}°</span>
                                    </div>
                                    <input
                                      type="range"
                                      min="-45"
                                      max="45"
                                      value={isTypographyLayer ? 0 : (activeRing.shear || 0)}
                                      disabled={isLocked || isTypographyLayer}
                                      onChange={(e) => updateSelectedRing('shear', parseInt(e.target.value))}
                                      style={{ width: '100%', accentColor: isTypographyLayer ? '#3c3c42' : '#007aff', height: '4px', cursor: (isLocked || isTypographyLayer) ? 'default' : 'pointer' }}
                                    />
                                  </div>
                                </div>

                                {/* Horizontal Scale & Vertical Scale */}
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', opacity: isTypographyLayer ? 0.4 : 1 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                      <span>H-Scale</span>
                                      <span>{isTypographyLayer ? 100 : (activeRing.hScale || 100)}%</span>
                                    </div>
                                    <input
                                      type="range"
                                      min="50"
                                      max="200"
                                      value={isTypographyLayer ? 100 : (activeRing.hScale || 100)}
                                      disabled={isLocked || isTypographyLayer}
                                      onChange={(e) => updateSelectedRing('hScale', parseInt(e.target.value))}
                                      style={{ width: '100%', accentColor: isTypographyLayer ? '#3c3c42' : '#007aff', height: '4px', cursor: (isLocked || isTypographyLayer) ? 'default' : 'pointer' }}
                                    />
                                  </div>
                                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', opacity: isTypographyLayer ? 0.4 : 1 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                      <span>V-Scale</span>
                                      <span>{isTypographyLayer ? 100 : (activeRing.vScale || 100)}%</span>
                                    </div>
                                    <input
                                      type="range"
                                      min="50"
                                      max="200"
                                      value={isTypographyLayer ? 100 : (activeRing.vScale || 100)}
                                      disabled={isLocked || isTypographyLayer}
                                      onChange={(e) => updateSelectedRing('vScale', parseInt(e.target.value))}
                                      style={{ width: '100%', accentColor: isTypographyLayer ? '#3c3c42' : '#007aff', height: '4px', cursor: (isLocked || isTypographyLayer) ? 'default' : 'pointer' }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </fieldset>
                          </div>
                        )}
                      </div>

                    {/* Collapsible BLUR Panel for Typography */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                      <div 
                        onClick={() => setBlurExpanded(prev => !prev)}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px', 
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                          {blurExpanded ? '▼' : '▶'}
                        </span>
                        <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Blur
                        </span>
                      </div>

                      {blurExpanded && activeRing && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '11px', color: '#dfdfe3', userSelect: 'none' }}>
                            <input
                              type="checkbox"
                              disabled={isTextLayerLocked()}
                              checked={activeRing.enableBlur === true}
                              onChange={(e) => updateSelectedRing('enableBlur', e.target.checked)}
                              style={{ width: '13px', height: '13px', accentColor: '#007aff', cursor: isTextLayerLocked() ? 'default' : 'pointer' }}
                            />
                            <span>Enable Blur Effect</span>
                          </label>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', opacity: (activeRing.enableBlur === true) ? 1 : 0.4 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                              <span>Gaussian Blur</span><span>{activeRing.gaussianBlur || 0}px</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="10"
                              step="0.5"
                              disabled={isTextLayerLocked() || activeRing.enableBlur !== true}
                              value={activeRing.gaussianBlur || 0}
                              onChange={(e) => updateSelectedRing('gaussianBlur', parseFloat(e.target.value))}
                              style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: (isTextLayerLocked() || activeRing.enableBlur !== true) ? 'default' : 'pointer' }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Collapsible Drop Shadow Panel */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                      <div 
                        onClick={() => setShadowExpanded(prev => !prev)}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px', 
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                          {shadowExpanded ? '▼' : '▶'}
                        </span>
                        <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Drop Shadow
                        </span>
                      </div>

                      {shadowExpanded && activeRing && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '11px', color: '#dfdfe3', userSelect: 'none' }}>
                            <input
                              type="checkbox"
                              disabled={isTextLayerLocked()}
                              checked={!!activeRing.shadowEnabled}
                              onChange={(e) => updateSelectedRing('shadowEnabled', e.target.checked)}
                              style={{ width: '13px', height: '13px', accentColor: '#007aff', cursor: isTextLayerLocked() ? 'default' : 'pointer' }}
                            />
                            <span>Enable Drop Shadow</span>
                          </label>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', opacity: activeRing.shadowEnabled ? 1 : 0.4 }}>
                            {/* Offsets in a grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                  <span>Offset X</span><span>{activeRing.shadowOffsetX !== undefined ? activeRing.shadowOffsetX : 2}px</span>
                                </div>
                                <input
                                  type="range"
                                  min="-20"
                                  max="20"
                                  step="1"
                                  disabled={isTextLayerLocked() || !activeRing.shadowEnabled}
                                  value={activeRing.shadowOffsetX !== undefined ? activeRing.shadowOffsetX : 2}
                                  onChange={(e) => updateSelectedRing('shadowOffsetX', parseInt(e.target.value))}
                                  style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: (isTextLayerLocked() || !activeRing.shadowEnabled) ? 'default' : 'pointer' }}
                                />
                              </div>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                  <span>Offset Y</span><span>{activeRing.shadowOffsetY !== undefined ? activeRing.shadowOffsetY : 2}px</span>
                                </div>
                                <input
                                  type="range"
                                  min="-20"
                                  max="20"
                                  step="1"
                                  disabled={isTextLayerLocked() || !activeRing.shadowEnabled}
                                  value={activeRing.shadowOffsetY !== undefined ? activeRing.shadowOffsetY : 2}
                                  onChange={(e) => updateSelectedRing('shadowOffsetY', parseInt(e.target.value))}
                                  style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: (isTextLayerLocked() || !activeRing.shadowEnabled) ? 'default' : 'pointer' }}
                                />
                              </div>
                            </div>

                            {/* Blur & Opacity in a grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                  <span>Blur Radius</span><span>{activeRing.shadowBlur !== undefined ? activeRing.shadowBlur : 3}px</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="20"
                                  step="0.5"
                                  disabled={isTextLayerLocked() || !activeRing.shadowEnabled}
                                  value={activeRing.shadowBlur !== undefined ? activeRing.shadowBlur : 3}
                                  onChange={(e) => updateSelectedRing('shadowBlur', parseFloat(e.target.value))}
                                  style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: (isTextLayerLocked() || !activeRing.shadowEnabled) ? 'default' : 'pointer' }}
                                />
                              </div>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                  <span>Opacity</span><span>{Math.round((activeRing.shadowOpacity !== undefined ? activeRing.shadowOpacity : 0.5) * 100)}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.05"
                                  disabled={isTextLayerLocked() || !activeRing.shadowEnabled}
                                  value={activeRing.shadowOpacity !== undefined ? activeRing.shadowOpacity : 0.5}
                                  onChange={(e) => updateSelectedRing('shadowOpacity', parseFloat(e.target.value))}
                                  style={{ width: '100%', accentColor: '#007aff', height: '4px', cursor: (isTextLayerLocked() || !activeRing.shadowEnabled) ? 'default' : 'pointer' }}
                                />
                              </div>
                            </div>

                            {/* Color picker */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '10px', color: '#8e8e93' }}>Shadow Color</span>
                              <input
                                type="color"
                                disabled={isTextLayerLocked() || !activeRing.shadowEnabled}
                                value={activeRing.shadowColor || '#000000'}
                                onChange={(e) => updateSelectedRing('shadowColor', e.target.value)}
                                style={{
                                  border: 'none',
                                  width: '32px',
                                  height: '18px',
                                  padding: 0,
                                  backgroundColor: 'transparent',
                                  cursor: (isTextLayerLocked() || !activeRing.shadowEnabled) ? 'default' : 'pointer'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={exportTypographySVG}
                      disabled={isTextLayerLocked()}
                      style={{
                        backgroundColor: '#3c3c42',
                        color: '#ffffff',
                        border: '1px solid #55555c',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        fontSize: '11px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        marginTop: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                    >
                      Export Typography (SVG)
                    </button>
                  </div>
                );
              })()}

                {/* 4. No selection fallback */}
                {!activeRing && activeTool !== 'text' && (
                  <div style={{ color: '#8e8e93', fontSize: '12px', fontStyle: 'italic', textAlign: 'center', marginTop: '40px' }}>
                    Select a layer or text on canvas to configure parameters.
                  </div>
                )}
              </div>
            )}

            {/* MATERIALS TAB (Positioned to the left of Slicer tab) */}
            {activeRightTab === 'materials' && (
              <div style={{ padding: '20px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Coin Material &amp; Relief Finish
                </div>

                {/* Metal Selection */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '11px', fontWeight: '600', color: '#dfdfe3' }}>Metal Alloy Finish</label>
                    <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#58a6ff', textTransform: 'capitalize' }}>
                      {metal || 'Gold'}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    {[
                      { id: 'gold', label: 'Gold', color: '#D4AF37', border: '#AA7C11' },
                      { id: 'silver', label: 'Silver', color: '#C0C0C0', border: '#8E9A9D' },
                      { id: 'bronze', label: 'Bronze', color: '#CD7F32', border: '#8C531B' },
                      { id: 'copper', label: 'Copper', color: '#B87333', border: '#7A3F18' },
                      { id: 'obsidian', label: 'Obsidian', color: '#1A1A1A', border: '#444444' },
                      { id: 'white', label: 'White Base', color: '#FFFFFF', border: '#B0B0B0' }
                    ].map(m => {
                      const isSelected = metal === m.id;
                      return (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => setMetal && setMetal(m.id)}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '10px 4px',
                            backgroundColor: isSelected ? 'rgba(22, 119, 255, 0.18)' : '#262629',
                            border: `2px solid ${isSelected ? '#1677ff' : '#3c3c42'}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            gap: '6px',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: m.color,
                            border: `1px solid ${m.border}`,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                          }} />
                          <span style={{ fontSize: '10px', fontWeight: isSelected ? '700' : '500', color: isSelected ? '#ffffff' : '#a2a2a8' }}>
                            {m.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Sculpting Relief Depth */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #2d2d30', paddingTop: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '11px', fontWeight: '600', color: '#dfdfe3' }}>Sculpting Relief Depth</label>
                    <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#58a6ff', textTransform: 'capitalize' }}>
                      {depth || 'Medium'}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                    {[
                      { id: 'low', label: 'Low (0.5mm)' },
                      { id: 'medium', label: 'Medium (1.0mm)' },
                      { id: 'high', label: 'High (1.5mm)' }
                    ].map(d => {
                      const isSelected = depth === d.id;
                      return (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => setDepth && setDepth(d.id)}
                          style={{
                            padding: '8px 4px',
                            backgroundColor: isSelected ? '#1677ff' : '#262629',
                            color: '#ffffff',
                            border: `1px solid ${isSelected ? '#1677ff' : '#3c3c42'}`,
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: isSelected ? '700' : '500',
                            cursor: 'pointer'
                          }}
                        >
                          {d.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Edge Style */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #2d2d30', paddingTop: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '11px', fontWeight: '600', color: '#dfdfe3' }}>Coin Edge Style</label>
                    <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#58a6ff', textTransform: 'capitalize' }}>
                      {edgeStyle || 'Reeded'}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                    {[
                      { id: 'reeded', label: 'Reeded' },
                      { id: 'smooth', label: 'Smooth' },
                      { id: 'knurled', label: 'Knurled' }
                    ].map(e => {
                      const isSelected = edgeStyle === e.id;
                      return (
                        <button
                          key={e.id}
                          type="button"
                          onClick={() => setEdgeStyle && setEdgeStyle(e.id)}
                          style={{
                            padding: '8px 4px',
                            backgroundColor: isSelected ? '#1677ff' : '#262629',
                            color: '#ffffff',
                            border: `1px solid ${isSelected ? '#1677ff' : '#3c3c42'}`,
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: isSelected ? '700' : '500',
                            cursor: 'pointer'
                          }}
                        >
                          {e.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Face Side */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #2d2d30', paddingTop: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '11px', fontWeight: '600', color: '#dfdfe3' }}>Active Face Side</label>
                    <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#58a6ff', textTransform: 'capitalize' }}>
                      {faceSide === 'heads' ? 'Heads (Obverse)' : 'Tails (Reverse)'}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                    {[
                      { id: 'heads', label: 'Heads (Obverse)' },
                      { id: 'tails', label: 'Tails (Reverse)' }
                    ].map(s => {
                      const isSelected = faceSide === s.id;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setFaceSide && setFaceSide(s.id)}
                          style={{
                            padding: '8px 4px',
                            backgroundColor: isSelected ? '#1677ff' : '#262629',
                            color: '#ffffff',
                            border: `1px solid ${isSelected ? '#1677ff' : '#3c3c42'}`,
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: isSelected ? '700' : '500',
                            cursor: 'pointer'
                          }}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* SLICER TAB */}
            {(activeRightTab === 'slicer' || activeRightTab === 'settings') && (
              <div style={{ padding: '20px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Slicer &amp; 3D Production Export
                </div>

                <div style={{ fontSize: '11px', color: '#a2a2a8', lineHeight: '1.5' }}>
                  Export ultra-high-resolution 4K heightmaps (4096×4096) with pure vector scaling and vector contour paths formatted for direct 3D slicing in Bambu Studio, OrcaSlicer, or PrusaSlicer.
                </div>

                {/* Export High-Res PNG Button */}
                <button
                  type="button"
                  data-testid="export-face-heightmap-btn"
                  onClick={handleExportHighResPNG}
                  style={{
                    backgroundColor: '#1b1b1d',
                    color: '#ffffff',
                    border: '1px solid #3c3c42',
                    borderRadius: '4px',
                    padding: '10px 14px',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    marginTop: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>Export Coin Face Heightmap (PNG)</span>
                    <span style={{ fontSize: '9px', backgroundColor: '#3b82f622', color: '#60a5fa', border: '1px solid #2563eb', padding: '1px 5px', borderRadius: '3px' }}>4K • 4096×4096</span>
                  </div>
                </button>

                {exportTypographySVG && (
                  <button
                    type="button"
                    onClick={exportTypographySVG}
                    style={{
                      backgroundColor: '#1b1b1d',
                      color: '#ffffff',
                      border: '1px solid #3c3c42',
                      borderRadius: '4px',
                      padding: '10px 14px',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 2 7 12 12 22 7 12 2" />
                      <polyline points="2 17 12 22 22 17" />
                      <polyline points="2 12 12 17 22 12" />
                    </svg>
                    <span>Export Vector Contour Paths (SVG)</span>
                  </button>
                )}

                <div style={{ marginTop: '10px', padding: '10px 12px', backgroundColor: '#161618', borderRadius: '6px', border: '1px solid #2d2d30', fontSize: '10px', color: '#8e8e93', lineHeight: '1.4' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#dfdfe3' }}>
                      <path d="M9 18h6" />
                      <path d="M10 22h4" />
                      <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
                    </svg>
                    <strong style={{ color: '#dfdfe3' }}>Slicing Recommendation:</strong>
                  </div>
                  For fine relief detail, use 0.08mm layer height or a 0.2mm nozzle in your slicer. High-contrast monochromatic images produce the crispest 3D embossing stamps.
                </div>
              </div>
            )}


          </div>
        </div>

      </div>

      {/* Bottom Bar */}
      <div style={{
        height: '24px',
        backgroundColor: '#2b2b2f',
        borderTop: '1px solid #3c3c42',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 12px',
        fontSize: '11px',
        color: '#8e8e93',
        userSelect: 'none',
        zIndex: 10
      }}>
        <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {activeTool === 'select' ? (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                  <polygon points="3 3 10 21 13 13 21 10 3 3" />
                </svg>
                <span>Select layers to edit properties, double-click name to rename.</span>
              </>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <span>Click on canvas to insert new {activeTool} element.</span>
              </>
            )}
          </span>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', borderLeft: '1px solid #3c3c42', paddingLeft: '10px' }}>
            <button
              type="button"
              disabled={undoStack.length === 0}
              onClick={handleUndo}
              style={{
                backgroundColor: 'transparent',
                color: undoStack.length === 0 ? '#55555c' : '#dfdfe3',
                border: 'none',
                fontSize: '10px',
                cursor: undoStack.length === 0 ? 'default' : 'pointer'
              }}
            >
              Undo
            </button>
            <span style={{ color: '#55555c' }}>/</span>
            <button
              type="button"
              disabled={redoStack.length === 0}
              onClick={handleRedo}
              style={{
                backgroundColor: 'transparent',
                color: redoStack.length === 0 ? '#55555c' : '#dfdfe3',
                border: 'none',
                fontSize: '10px',
                cursor: redoStack.length === 0 ? 'default' : 'pointer'
              }}
            >
              Redo
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span>Design Face @ {Math.round((canvasSize / 600) * 100)}%</span>
          <input
            type="range"
            min="300"
            max="900"
            step="10"
            value={canvasSize}
            onChange={(e) => setCanvasSize(parseInt(e.target.value))}
            style={{ width: '80px', accentColor: '#007aff', height: '4px', cursor: 'pointer' }}
          />
        </div>
      </div>

      {/* Asset Picker Modal */}
      {showAssetModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000000,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsAssetDragOver(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsAssetDragOver(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setIsAssetDragOver(false);
              if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                handleUploadAssetFiles(e.dataTransfer.files);
              }
            }}
            style={{
              backgroundColor: '#1b1b1d',
              border: isAssetDragOver ? '2px dashed #3b82f6' : '1px solid #3c3c42',
              borderRadius: '8px',
              width: '740px',
              maxWidth: '92vw',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 12px 36px rgba(0,0,0,0.65)',
              overflow: 'hidden',
              transition: 'border 0.15s ease'
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #2d2d30', padding: '12px 18px', backgroundColor: '#212124' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#dfdfe3' }}>
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#dfdfe3', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Asset Library & Upload
                </span>
                <span style={{ fontSize: '11px', color: '#8e8e93', marginLeft: '6px' }}>
                  ({(localStudioAssets || []).length} {(localStudioAssets || []).length === 1 ? 'asset' : 'assets'})
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowAssetModal(false);
                  setIsChangingImageSource(false);
                  setChangingImageRingId(null);
                  setAssetUploadError('');
                  setAssetUploadSuccess('');
                }}
                style={{ backgroundColor: 'transparent', border: 'none', color: '#8e8e93', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px' }}
                title="Close Modal"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Action & Upload Bar */}
            <div style={{ padding: '10px 18px', borderBottom: '1px solid #2d2d30', display: 'flex', gap: '8px', backgroundColor: '#18181a', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search assets..."
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                style={{
                  flex: 1,
                  backgroundColor: '#141416',
                  border: '1px solid #3c3c42',
                  borderRadius: '4px',
                  padding: '6px 10px',
                  color: '#fff',
                  fontSize: '11px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              {localSearch && (
                <button
                  type="button"
                  onClick={() => setLocalSearch('')}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#8e8e93',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    marginRight: '4px'
                  }}
                >
                  Clear
                </button>
              )}
              <select
                value={localType}
                onChange={(e) => setLocalType(e.target.value)}
                style={{
                  backgroundColor: '#141416',
                  border: '1px solid #3c3c42',
                  borderRadius: '4px',
                  padding: '5px 10px',
                  color: '#ffffff',
                  fontSize: '11px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Formats</option>
                <option value="svg">Vector SVGs (.svg)</option>
                <option value="raster">Images (PNG, JPG, WebP)</option>
              </select>

              {/* Hidden File Input & Upload Trigger */}
              <input
                ref={assetFileInputRef}
                type="file"
                accept="image/*,.svg"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleUploadAssetFiles(e.target.files);
                    e.target.value = '';
                  }
                }}
              />
              <button
                type="button"
                disabled={isUploadingAsset}
                onClick={() => assetFileInputRef.current && assetFileInputRef.current.click()}
                data-testid="upload-new-asset-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  backgroundColor: isUploadingAsset ? '#3c3c42' : '#1677ff',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  fontSize: '11px',
                  fontWeight: '600',
                  cursor: isUploadingAsset ? 'not-allowed' : 'pointer',
                  opacity: isUploadingAsset ? 0.7 : 1,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  transition: 'all 0.15s'
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span>{isUploadingAsset ? 'Uploading...' : 'Upload Asset'}</span>
              </button>
            </div>

            {/* Feedback & Error Alerts */}
            {isUploadingAsset && (
              <div style={{ padding: '8px 18px', backgroundColor: 'rgba(59, 130, 246, 0.15)', borderBottom: '1px solid rgba(59, 130, 246, 0.3)', color: '#93c5fd', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ animation: 'spin 1s linear infinite' }}>
                  <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.2)" />
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="#93c5fd" />
                </svg>
                <span>Uploading image asset(s)...</span>
              </div>
            )}
            {assetUploadError && (
              <div style={{ padding: '8px 18px', backgroundColor: 'rgba(239, 68, 68, 0.15)', borderBottom: '1px solid rgba(239, 68, 68, 0.3)', color: '#f87171', fontSize: '11px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{assetUploadError}</span>
              </div>
            )}
            {assetUploadSuccess && (
              <div style={{ padding: '8px 18px', backgroundColor: 'rgba(34, 197, 94, 0.15)', borderBottom: '1px solid rgba(34, 197, 94, 0.3)', color: '#4ade80', fontSize: '11px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span>{assetUploadSuccess}</span>
              </div>
            )}

            {/* Assets Grid */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', maxHeight: '420px', minHeight: '160px', backgroundColor: '#141416' }}>
              {(() => {
                const assetsToDisplay = (localStudioAssets || []);
                const filtered = assetsToDisplay.filter(asset => {
                  if (!asset) return false;
                  const filename = (asset.original_name || asset.original_filename || asset.filename || '').toLowerCase();
                  const mime = (asset.mime_type || '').toLowerCase();
                  const isSvg = mime.includes('svg') || filename.endsWith('.svg');

                  const matchQuery = !localSearch || filename.includes(localSearch.toLowerCase());
                  let matchType = true;
                  if (localType === 'svg') matchType = isSvg;
                  else if (localType === 'raster') matchType = !isSvg;
                  return matchQuery && matchType;
                });

                if (filtered.length === 0) {
                  return (
                    <div style={{ gridColumn: 'span 5', textAlign: 'center', color: '#8e8e93', fontSize: '12px', padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#55555c' }}>
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                      </svg>
                      <span style={{ color: '#dfdfe3', fontWeight: '600' }}>
                        {assetsToDisplay.length === 0 ? 'No assets uploaded yet' : 'No matching assets found'}
                      </span>
                      <span style={{ fontSize: '11px', color: '#6b7280' }}>
                        {assetsToDisplay.length === 0
                          ? 'Click "+ Upload Asset" above or drag and drop PNG, JPG, or SVG files here.'
                          : 'Try clearing your search query or switching asset format filters.'}
                      </span>
                    </div>
                  );
                }

                return filtered.map((asset) => {
                  const filename = asset.original_name || asset.original_filename || asset.filename || 'Emblem Asset';
                  const mimeType = asset.mime_type || asset.file_type || '';
                  const isSvg = mimeType.toLowerCase().includes('svg') || filename.toLowerCase().endsWith('.svg');
                  const ext = isSvg ? 'SVG' : (filename.split('.').pop() || 'IMG').toUpperCase();

                  const rawPath = asset.file_url || asset.location || asset.storage_location || '';
                  let cleanRelativePath = rawPath;
                  if (rawPath.includes('/uploads/')) {
                    cleanRelativePath = rawPath.substring(rawPath.indexOf('/uploads/')).split('?')[0];
                  }
                  const base = API_BASE ? API_BASE.replace(/\/+$/, '') : '';
                  let assetUrl = cleanRelativePath.startsWith('http')
                    ? cleanRelativePath
                    : (cleanRelativePath.startsWith('/') ? `${base}${cleanRelativePath}` : `${base}/${cleanRelativePath}`);
                  if (assetUrl && !assetUrl.startsWith('data:') && !assetUrl.startsWith('blob:')) {
                    const separator = assetUrl.includes('?') ? '&' : '?';
                    assetUrl = `${assetUrl}${separator}cors=true`;
                  }

                  return (
                    <div
                      key={asset.id || asset.filename}
                      data-testid={`asset-card-${asset.id}`}
                      onClick={() => {
                        if (isChangingImageSource && changingImageRingId) {
                          saveHistoryState();
                          let updatedRing = null;
                          setRings(prevRings => prevRings.map(r => {
                            if (r.id === changingImageRingId) {
                              updatedRing = {
                                ...r,
                                imageUrl: assetUrl,
                                name: filename ? (filename.includes('.') ? (filename.split('.').slice(0, -1).join('.') || filename) : filename) : r.name,
                                imgPanX: 0,
                                imgPanY: 0,
                                imgZoom: 1,
                                cropApplied: false
                              };
                              return updatedRing;
                            }
                            return r;
                          }));
                          setShowAssetModal(false);
                          setIsChangingImageSource(false);
                          setChangingImageRingId(null);
                          if (updatedRing) {
                            openImageAdjustModal(updatedRing);
                          }
                          return;
                        }
                        saveHistoryState();
                        handleAssetDoubleClick(assetUrl, filename);
                        setShowAssetModal(false);
                        setActiveTool('select');
                        setActiveRightTab('properties');
                      }}
                      title={`Click to use "${filename}" on coin face`}
                      style={{
                        backgroundColor: '#262629',
                        border: '1px solid #3c3c42',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '8px',
                        height: '120px',
                        boxSizing: 'border-box'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#1677ff';
                        e.currentTarget.style.backgroundColor = '#2c2c30';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#3c3c42';
                        e.currentTarget.style.backgroundColor = '#262629';
                        e.currentTarget.style.transform = 'none';
                      }}
                    >
                      <div style={{ width: '100%', height: '70px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#111111', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
                        {isSvg ? (
                          <img src={assetUrl} crossOrigin="anonymous" alt={filename} style={{ maxWidth: '85%', maxHeight: '85%', objectFit: 'contain' }} />
                        ) : (
                          <img src={assetUrl} crossOrigin="anonymous" alt={filename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        )}
                        <span style={{
                          position: 'absolute',
                          top: '3px',
                          right: '3px',
                          backgroundColor: 'rgba(0,0,0,0.65)',
                          color: '#dfdfe3',
                          fontSize: '8px',
                          padding: '1px 4px',
                          borderRadius: '3px',
                          fontWeight: '700'
                        }}>
                          {ext}
                        </span>
                      </div>
                      <span style={{ fontSize: '10px', color: '#dfdfe3', marginTop: '6px', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }} title={filename}>
                        {filename}
                      </span>
                    </div>
                  );
                });
              })()}
            </div>

            {/* Footer */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #2d2d30', padding: '10px 16px', backgroundColor: '#1b1b1d' }}>
              <span style={{ fontSize: '11px', color: '#8e8e93' }}>
                💡 Click an asset to place it on your coin face canvas. Drag &amp; drop image files to upload anytime.
              </span>
              <button
                type="button"
                onClick={() => {
                  setShowAssetModal(false);
                  setIsChangingImageSource(false);
                  setChangingImageRingId(null);
                  setAssetUploadError('');
                  setAssetUploadSuccess('');
                }}
                style={{
                  padding: '6px 16px',
                  fontSize: '11px',
                  backgroundColor: '#3c3c42',
                  color: '#dfdfe3',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  transition: 'background-color 0.15s'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Face Image Pan & Zoom Crop Modal */}
      <FaceImageCropModal
        isOpen={showImageCropModal}
        ring={cropAdjustingRing}
        onApply={handleApplyImageCrop}
        onClose={() => {
          setShowImageCropModal(false);
          setCropAdjustingRing(null);
        }}
      />

      {/* Blocking Saving Modal Overlay */}
      {isSaving && (
        <div
          data-testid="saving-face-modal"
          data-html2canvas-ignore="true"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(10, 10, 14, 0.72)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000005,
            cursor: 'wait',
            userSelect: 'none',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
        >
          <div
            style={{
              background: '#1b1b1d',
              border: '1px solid #3c3c42',
              borderRadius: '12px',
              width: '360px',
              maxWidth: '90vw',
              padding: '24px 22px',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.75)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: '14px'
            }}
          >
            <div
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                backgroundColor: 'rgba(22, 119, 255, 0.14)',
                border: '1px solid rgba(22, 119, 255, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1677ff"
                strokeWidth="2.5"
                strokeLinecap="round"
                style={{ animation: 'spin 0.9s linear infinite' }}
              >
                <circle cx="12" cy="12" r="9" stroke="rgba(22, 119, 255, 0.25)" />
                <path d="M12 3a9 9 0 0 1 9 9" stroke="#38bdf8" />
              </svg>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#ffffff' }}>
                Saving Face Design...
              </h3>
              <p style={{ margin: 0, fontSize: '12px', color: '#38bdf8', fontWeight: '600' }}>
                Rendering high-resolution coin face &amp; saving...
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#8e8e93', lineHeight: '1.45' }}>
                Please wait while the system finishes saving your changes.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
